import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  FileText,
  Download,
  Eye,
  ArrowLeft,
  Calendar,
  Loader2,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Video,
  Radio,
  Users,
  BarChart3,
  TrendingUp,
  Image as ImageIcon,
  Palette,
  X,
  Maximize2,
  RotateCcw,
  Trash2,
  Tv,
  Share2
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import clientReportsAPI from '../api/clientReports';
import { campaignsAPI } from '../api/campaigns';
import { reportsAPI } from '../api/reports';
import { gamesAPI } from '../api/games';
import ImageBlockPicker from './reports/ImageBlockPicker';
import GameBrandingEditor from './GameBrandingEditor';
import ContentGridView from './ContentGridView';
import VideoBySectionView from './VideoBySectionView';
import SocialMediaBySectionView from './SocialMediaBySectionView';
import Footer from './Footer';
import StreamingBySectionView from './StreamingBySectionView';
import PlatformIcon from './PlatformIcon';
import YouTubeEstimateTooltip from './YouTubeEstimateTooltip';
import { aggregateContentMetrics, impressionsFor } from '../utils/reportMetrics';
import {
  formatNumber,
  formatFullNumber,
  formatCurrency,
  formatPercent,
  formatWatchHours,
  formatDate,
  getPlatformColor
} from '../utils/formatters';

// API base URL for thumbnail resolution
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Cache version - increment this when thumbnails are updated to bust browser cache
const CACHE_VERSION = 'v2';

// Helper to resolve thumbnail URLs (local cached thumbnails start with /api/)
// In production, frontend is served by backend so /api/ paths work as relative URLs
const resolveThumbnailUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    const base = window.location.hostname === 'localhost' ? API_BASE_URL : '';
    return `${base}${url}?${CACHE_VERSION}`;
  }
  return url;
};

// ─── Dark Theme Constants (matching ClientPortalPublicV2) ─────────────────
const DARK_INPUT = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderColor: 'rgba(255,255,255,0.15)',
  color: '#fff',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
};
const CHART_DARK = {
  grid: 'rgba(255,255,255,0.1)',
  tick: { fill: 'rgba(255,255,255,0.5)', fontSize: 12 },
  tooltip: {
    backgroundColor: 'rgba(0,0,0,0.85)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: '#fff',
  },
};

// Group content by day and aggregate by platform for timeline charts
const groupByDay = (content, isStreaming = false) => {
  const groups = {};

  content.forEach(item => {
    const date = new Date(item.postDate);
    const key = date.toISOString().split('T')[0]; // YYYY-MM-DD
    const platform = (item.platform || 'other').toLowerCase();

    if (!groups[key]) {
      groups[key] = {
        period: key,
        views: 0,
        likes: 0,
        comments: 0,
        shares: 0,
        avgCcv: [],
        peakCcv: [],
        watchHours: 0,
        contentCount: 0,
        youtube: { views: 0, likes: 0, avgCcv: [], watchHours: 0 },
        twitch: { views: 0, likes: 0, avgCcv: [], watchHours: 0 },
        tiktok: { views: 0, likes: 0, avgCcv: [], watchHours: 0 },
        instagram: { views: 0, likes: 0, avgCcv: [], watchHours: 0 }
      };
    }

    // Aggregate totals
    groups[key].views += Number(item.views) || 0;
    groups[key].likes += Number(item.likes) || 0;
    groups[key].comments += Number(item.comments) || 0;
    groups[key].shares += Number(item.shares) || 0;
    groups[key].watchHours += Number(item.watchHours) || 0;
    groups[key].contentCount += 1;

    if (isStreaming && item.avgCcv) {
      groups[key].avgCcv.push(Number(item.avgCcv));
    }
    if (isStreaming && item.peakCcv) {
      groups[key].peakCcv.push(Number(item.peakCcv));
    }

    // Platform-specific aggregation
    if (groups[key][platform]) {
      groups[key][platform].views += Number(item.views) || 0;
      groups[key][platform].likes += Number(item.likes) || 0;
      groups[key][platform].watchHours += Number(item.watchHours) || 0;
      if (isStreaming && item.avgCcv) {
        groups[key][platform].avgCcv.push(Number(item.avgCcv));
      }
    }
  });

  return Object.values(groups).map(g => ({
    ...g,
    avgCcv: g.avgCcv.length > 0 ? Math.round(g.avgCcv.reduce((a, b) => a + b, 0) / g.avgCcv.length) : 0,
    peakCcv: g.peakCcv.length > 0 ? Math.max(...g.peakCcv) : 0,
    youtube: {
      ...g.youtube,
      avgCcv: g.youtube.avgCcv.length > 0 ? Math.round(g.youtube.avgCcv.reduce((a, b) => a + b, 0) / g.youtube.avgCcv.length) : 0
    },
    twitch: {
      ...g.twitch,
      avgCcv: g.twitch.avgCcv.length > 0 ? Math.round(g.twitch.avgCcv.reduce((a, b) => a + b, 0) / g.twitch.avgCcv.length) : 0
    },
    tiktok: {
      ...g.tiktok,
      avgCcv: g.tiktok.avgCcv.length > 0 ? Math.round(g.tiktok.avgCcv.reduce((a, b) => a + b, 0) / g.tiktok.avgCcv.length) : 0
    },
    instagram: {
      ...g.instagram,
      avgCcv: g.instagram.avgCcv.length > 0 ? Math.round(g.instagram.avgCcv.reduce((a, b) => a + b, 0) / g.instagram.avgCcv.length) : 0
    }
  })).sort((a, b) => new Date(a.period) - new Date(b.period));
};

// Transform campaign data to match report structure
const transformCampaignToReportData = (campaign) => {
  console.log('=== transformCampaignToReportData CALLED ===');
  console.log('Input campaign:', campaign);
  if (!campaign) {
    console.warn('Campaign is null/undefined!');
    return {
      campaign: {},
      summary: { reach: 0, views: 0, totalEngagement: 0, engagementRate: 0, avgCcv: 0, watchHours: 0 },
      videoSection: { metrics: { count: 0, totalViews: 0, totalLikes: 0, totalComments: 0, totalShares: 0, engagementRate: 0 }, timeline: [], content: [] },
      streamingSection: { metrics: { count: 0, averageCcv: 0, peakCcvMax: 0, totalWatchHours: 0, totalImpressions: 0, totalVodViews: 0 }, timeline: [], content: [] },
      platforms: [],
      creators: []
    };
  }

  const videoContent = [];
  const streamingContent = [];
  const creators = [];
  const platforms = new Map();
  const platformCreators = new Map(); // Track unique creators per platform
  let totalReach = 0;
  let totalViews = 0;
  let youtubeViews = 0;
  let tiktokViews = 0;
  let instagramViews = 0;
  let twitchViews = 0;
  let totalEngagement = 0;

  // Process influencers and their content
  console.log('Raw campaign.influencers:', campaign.influencers);
  if (campaign.influencers && Array.isArray(campaign.influencers)) {
    campaign.influencers.forEach((influencer, idx) => {
      console.log(`Influencer ${idx} raw data:`, influencer);
      if (!influencer || !influencer.creator) {
        console.warn(`Influencer ${idx} missing creator:`, influencer);
        return;
      }

      // Extract creator info for use in content items
      const creatorName = influencer.creator?.name || influencer.creator?.creatorName || influencer.creator?.handle || influencer.creator?.username || 'Unknown';
      const creatorImage = influencer.creator?.profileImageUrl || influencer.creator?.profileUrl || influencer.creator?.image || influencer.creator?.avatar || '';
      const creatorRegion = influencer.creator?.region || influencer.creator?.country || 'Unknown';
      

      const creatorStats = {
        creator: influencer.creator,
        metrics: {
          contentCount: (influencer.content && influencer.content.length) || 0,
          totalContentCount: influencer.metrics?.totalContentCount || (influencer.content && influencer.content.length) || 0,
          views: 0,
          likes: 0,
          comments: 0,
          shares: 0,
          engagement: 0,
          engagementRate: 0,
          totalEngagement: 0,
          avgCcv: 0,
          watchHours: 0
        }
      };

      if (influencer.content && Array.isArray(influencer.content)) {
        influencer.content.forEach((content, cidx) => {
          if (!content) return;
          
          console.log(`Content ${cidx}:`, content);
          console.log(`Content ${cidx} segments:`, content.segments);

          // Streams: live/ended stream rows, anything with a CCV, and every Twitch/Kick
          // row (both platforms are stream-only; a Kick VOD may have no CCV typed yet).
          if (content.contentType === 'stream' || content.contentType === 'vod' || content.streamAvgCcv ||
              ['twitch', 'kick'].includes((content.platform || '').toLowerCase())) {
            // Get segment data
            let segmentDuration = 0;
            let segmentImpressions = 0;
            let segmentAvgCcv = 0;
            let segmentPeakCcv = 0;
            
            if (content.segments && content.segments.length > 0) {
              // Use first segment's duration, sum impressions and avg CCV from all segments
              segmentDuration = Number(content.segments[0].durationMinutes) || 0;
              for (const segment of content.segments) {
                segmentImpressions += Number(segment.impressions) || 0;
                segmentAvgCcv += Number(segment.avgCcv) || 0;
                segmentPeakCcv = Math.max(segmentPeakCcv, Number(segment.peakCcv) || 0);
              }
            }
            
            streamingContent.push({
              id: content.id || '',
              title: content.title || 'Untitled',
              creatorName,
              creatorImage,
              creatorRegion,
              creatorId: influencer.creator?.id,
              thumbnailUrl: content.thumbnailUrl || '',
              creator: {
                id: influencer.creator?.id,
                name: creatorName,
                profileImageUrl: creatorImage
              },
              platform: content.platform || 'Unknown',
              postDate: content.postDate,
              isPaid: content.isPaid || false,
              streamDuration: segmentDuration > 0 ? segmentDuration : Number(content.streamDuration) || 0,
              avgCcv: segmentAvgCcv > 0 ? Math.round(segmentAvgCcv / (content.segments?.length || 1)) : Number(content.streamAvgCcv) || 0,
              peakCcv: segmentPeakCcv > 0 ? segmentPeakCcv : Number(content.streamPeakCcv) || 0,
              watchHours: Number(content.watchHours) || Number(content.streamWatchHours) || 0,
              impressions: segmentImpressions > 0 ? segmentImpressions : impressionsFor(content, Number(content.views) || 0),
              vodViews: Number(content.vodViews || content.vodViewCount || 0)
            });
            
            // DEBUG
            if (streamingContent.length === 1) {
              console.log('🎬 First streaming content item:', {
                title: content.title,
                platform: content.platform,
                views: content.views,
                vodViews: content.vodViews,
                vodViewCount: content.vodViewCount,
                final_vodViews: Number(content.vodViews || content.vodViewCount || 0)
              });
            }
            creatorStats.metrics.avgCcv += Number(content.streamAvgCcv) || 0;
            creatorStats.metrics.watchHours += Number(content.watchHours) || Number(content.streamWatchHours) || 0;
          } else {
            videoContent.push({
              id: content.id || '',
              title: content.title || 'Untitled',
              creatorName,
              creatorImage,
              creatorRegion,
              creatorId: influencer.creator?.id,
              thumbnailUrl: content.thumbnailUrl || '',
              creator: {
                id: influencer.creator?.id,
                name: creatorName,
                profileImageUrl: creatorImage
              },
              platform: content.platform || 'Unknown',
              postDate: content.postDate,
              isPaid: content.isPaid || false,
              views: Number(content.views) || 0,
              likes: Number(content.likes) || 0,
              comments: Number(content.comments) || 0,
              shares: Number(content.shares) || 0,
              impressions: impressionsFor(content, Number(content.views) || 0),
              engagementRate: Number(content.engagementRate) || 0
            });
            creatorStats.metrics.views += Number(content.views) || 0;
            creatorStats.metrics.likes += Number(content.likes) || 0;
            creatorStats.metrics.comments += Number(content.comments) || 0;
            creatorStats.metrics.shares += Number(content.shares) || 0;

            const views = Number(content.views) || 0;
            totalViews += views;
            totalEngagement += (Number(content.likes) || 0) + (Number(content.comments) || 0) + (Number(content.shares) || 0);

            // Track platform-specific views
            const platformLower = (content.platform || '').toLowerCase();
            if (platformLower === 'youtube') {
              youtubeViews += views;
            } else if (platformLower === 'tiktok') {
              tiktokViews += views;
            } else if (platformLower === 'instagram') {
              instagramViews += views;
            } else if (platformLower === 'twitch' || platformLower === 'kick') {
              // Kick streams are reported alongside Twitch under the stream/VOD total.
              twitchViews += views;
            }
          }

          // Platform breakdown
          const platform = content.platform || 'Unknown';
          if (!platforms.has(platform)) {
            platforms.set(platform, {
              platform,
              creatorCount: 0,
              contentCount: 0,
              views: 0,
              likes: 0,
              comments: 0,
              shares: 0
            });
            platformCreators.set(platform, new Set());
          }
          const p = platforms.get(platform);
          p.contentCount++;
          p.views += Number(content.views) || 0;
          p.likes += Number(content.likes) || 0;
          p.comments += Number(content.comments) || 0;
          p.shares += Number(content.shares) || 0;

          // Track unique creator for this platform
          if (creatorName && !platformCreators.get(platform).has(creatorName)) {
            platformCreators.get(platform).add(creatorName);
            p.creatorCount++;
          }
        });
      }

      creatorStats.metrics.engagement = creatorStats.metrics.likes + creatorStats.metrics.comments + creatorStats.metrics.shares;
      creatorStats.metrics.totalEngagement = creatorStats.metrics.engagement;
      creatorStats.metrics.engagementRate = creatorStats.metrics.views > 0 
        ? (creatorStats.metrics.engagement / creatorStats.metrics.views) * 100
        : 0;
      creators.push(creatorStats);
      totalReach += (influencer.creator?.followerCount && Number(influencer.creator.followerCount)) || 0;
    });
  }

  const summary = {
    reach: totalReach,
    views: totalViews,
    youtubeViews,
    tiktokViews,
    instagramViews,
    twitchViews,
    totalEngagement,
    engagementRate: totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0,
    avgCcv: Math.round(streamingContent.reduce((sum, s) => sum + (s.avgCcv || 0), 0)),
    watchHours: streamingContent.reduce((sum, s) => sum + (s.watchHours || 0), 0),
    campaign: {
      name: campaign.name || 'Campaign',
      game: campaign.game?.title,
      clientName: campaign.clientName,
      startDate: campaign.startDate,
      endDate: campaign.endDate
    }
  };

  // Split summaries: Organic (not paid) vs Sponsored (paid). Same hero-card shape
  // as `summary` above, computed from the paid/organic subset of content.
  const summarize = (videos, streams) => {
    const byPlatform = (plat) => videos.filter(v => (v.platform || '').toLowerCase() === plat).reduce((s, v) => s + (v.views || 0), 0);
    const vViews = videos.reduce((s, v) => s + (v.views || 0), 0);
    const eng = videos.reduce((s, v) => s + (v.likes || 0) + (v.comments || 0) + (v.shares || 0), 0);
    return {
      reach: 0,
      views: vViews,
      youtubeViews: byPlatform('youtube'),
      tiktokViews: byPlatform('tiktok'),
      instagramViews: byPlatform('instagram'),
      twitchViews: byPlatform('twitch'),
      totalEngagement: eng,
      engagementRate: vViews > 0 ? (eng / vViews) * 100 : 0,
      avgCcv: Math.round(streams.reduce((s, st) => s + (st.avgCcv || 0), 0)),
      watchHours: streams.reduce((s, st) => s + (st.watchHours || 0), 0),
      vodViews: streams.reduce((s, st) => s + (st.vodViews || 0), 0),
      totalImpressions: videos.reduce((s, v) => s + (v.impressions || 0), 0) + streams.reduce((s, st) => s + (st.impressions || 0), 0),
      videoCount: videos.length,
      streamCount: streams.length,
      contentCount: videos.length + streams.length,
      campaign: summary.campaign,
    };
  };
  const organicSummary = summarize(videoContent.filter(v => !v.isPaid), streamingContent.filter(s => !s.isPaid));
  const sponsoredSummary = summarize(videoContent.filter(v => v.isPaid), streamingContent.filter(s => s.isPaid));

  // Generate timeline data for charts
  const videoTimeline = groupByDay(videoContent, false);
  const streamTimeline = groupByDay(streamingContent, true);

  // Convert platforms Map to array and calculate additional stats
  const platformsArray = Array.from(platforms.values()).map(p => {
    const totalPlatformEngagement = p.likes + p.comments + p.shares;
    return {
      ...p,
      engagementRate: p.views > 0 ? (totalPlatformEngagement / p.views) * 100 : 0,
      viewsPercentage: totalViews > 0 ? Math.round((p.views / totalViews) * 100) : 0
    };
  }).sort((a, b) => b.views - a.views);

  return {
    campaign: summary.campaign,
    summary,
    organicSummary,
    sponsoredSummary,
    videoSection: {
      metrics: {
        count: videoContent.length,
        totalViews,
        totalLikes: videoContent.reduce((sum, v) => sum + (v.likes || 0), 0),
        totalComments: videoContent.reduce((sum, v) => sum + (v.comments || 0), 0),
        totalShares: videoContent.reduce((sum, v) => sum + (v.shares || 0), 0),
        engagementRate: totalViews > 0 ? (totalEngagement / totalViews) * 100 : 0
      },
      timeline: videoTimeline,
      content: videoContent
    },
    streamingSection: {
      metrics: {
        count: streamingContent.length,
        averageCcv: streamingContent.length > 0
          ? Math.round(streamingContent.reduce((sum, s) => sum + (s.avgCcv || 0), 0) / streamingContent.length)
          : 0,
        peakCcvMax: streamingContent.length > 0
          ? Math.max(...streamingContent.map(s => s.peakCcv || 0))
          : 0,
        totalWatchHours: streamingContent.reduce((sum, s) => sum + (s.watchHours || 0), 0),
        totalImpressions: streamingContent.reduce((sum, s) => sum + (s.impressions || 0), 0),
        totalVodViews: streamingContent.reduce((sum, s) => sum + (s.vodViews || 0), 0)
      },
      timeline: streamTimeline,
      content: streamingContent
    },
    platforms: platformsArray,
    creators
  };
};

const ClientReportEditor = ({ campaignId, reportId: reportIdProp, onBack }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [createdReportId, setCreatedReportId] = useState(null);
  const reportId = reportIdProp || createdReportId;
  const isEditing = !!reportId;
  
  console.log('=== ClientReportEditor Component ===');
  console.log('Props - campaignId:', campaignId, 'reportId:', reportId);
  console.log('isEditing:', isEditing);
  console.log('Query will be enabled:', !isEditing && !!campaignId);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    reportPeriodStart: '',
    reportPeriodEnd: '',
    activitiesCompleted: '',
    upcomingActivities: '',
    overallAnalysis: '',
    videoAnalysis: '',
    streamingAnalysis: '',
    socialMediaAnalysis: '',
    status: 'draft'
  });

  const [expandedSections, setExpandedSections] = useState({
    executive: true,
    video: true,
    socialMedia: true,
    streaming: true,
    platforms: true,
    creators: true
  });
  
  // Column visibility toggles for video and streaming tables
  const [visibleColumns, setVisibleColumns] = useState({
    video: {
      creator: true,
      title: true,
      platform: true,
      date: true,
      views: true,
      impressions: true,
      likes: true,
      comments: true,
      engagementRate: true
    },
    streaming: {
      creator: true,
      title: true,
      platform: true,
      date: true,
      duration: true,
      avgCcv: true,
      peakCcv: true,
      impressions: true,
      watchHours: true,
      vodViews: true
    }
  });
  
  const toggleColumnVisibility = (sectionType, columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [sectionType]: {
        ...prev[sectionType],
        [columnName]: !prev[sectionType][columnName]
      }
    }));
  };
  
  const [videoContentRows, setVideoContentRows] = useState(20);
  const [streamingContentRows, setStreamingContentRows] = useState(20);

  const [excludedContentIds, setExcludedContentIds] = useState([]);
  const [showExcludedDropdown, setShowExcludedDropdown] = useState(false);

  // Metric visibility toggles for PDF export
  const [metricVisibility, setMetricVisibility] = useState({
    // Executive Summary metrics
    totalEstimatedImpressions: true,
    totalViews: true,
    totalLikes: true,
    engagementRate: true,
    avgCcv: true,
    watchHours: true,
    // Video section metrics
    videoViews: true,
    videoLikes: true,
    videoComments: true,
    videoShares: true,
    videoEngRate: true,
    videoCount: true,
    // Social media section metrics
    socialMediaViews: true,
    socialMediaLikes: true,
    socialMediaComments: true,
    socialMediaShares: true,
    socialMediaEngRate: true,
    // Streaming section metrics
    peakCcv: true,
    vodViews: true,
    streamCount: true
  });

  const toggleMetricVisibility = (metric) => {
    setMetricVisibility(prev => ({ ...prev, [metric]: !prev[metric] }));
  };

  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'
  const [showPreview, setShowPreview] = useState(false);

  // Image blocks and branding state
  const [contentBlocks, setContentBlocks] = useState([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imageInsertPosition, setImageInsertPosition] = useState(null); // 'header', 'after-executive', 'after-video', 'after-streaming'
  const [showBrandingEditor, setShowBrandingEditor] = useState(false);
  const [gameBranding, setGameBranding] = useState(null);
  const [gameId, setGameId] = useState(null);
  const brandingLoadedRef = useRef(false); // Track if branding has been loaded to prevent refetch after save
  // Same guard for content blocks (images): load from the server ONCE. Without this,
  // a background refetch (window focus, post-save invalidation) resets contentBlocks
  // to the last-saved copy and wipes images the user just added ("re-add throughout").
  const contentBlocksLoadedRef = useRef(false);
  // Same guard for exclusions/highlights: load from the server ONCE. Without this,
  // a background refetch (window focus, post-save invalidation after typing) resets
  // excludedContentIds to the last-saved copy and re-hides content the user just
  // restored ("restore, type, it vanishes again").
  const exclusionsLoadedRef = useRef(false);
  const [showFullPreview, setShowFullPreview] = useState(false);

  // Sort state for video and streaming content (lifted from ContentGridView for preview sync)
  const [videoSortBy, setVideoSortBy] = useState('views-high');
  const [streamingSortBy, setStreamingSortBy] = useState('avgCcv-high');
  const [socialMediaSortBy, setSocialMediaSortBy] = useState('views-high');

  // Highlighted content for top performers sections in PDF
  const [highlightedVideoIds, setHighlightedVideoIds] = useState([]);
  const [highlightedStreamIds, setHighlightedStreamIds] = useState([]);

  // Fetch existing report if editing
  const { data: existingReport, isLoading: reportLoading } = useQuery({
    queryKey: ['client-report', reportId],
    queryFn: () => clientReportsAPI.getReport(reportId).then(r => r.data),
    enabled: isEditing
  });

  // Fetch full report data for preview and PDF
  const { data: fullReportData, isLoading: dataLoading, refetch: refetchFullData } = useQuery({
    queryKey: ['client-report-full', reportId],
    queryFn: () => clientReportsAPI.getFullReportData(reportId).then(r => r.data),
    enabled: isEditing && !!reportId
  });

  // Fetch campaign data for new reports (to show content tables)
  const { data: campaignFullData, isLoading: campaignLoading } = useQuery({
    queryKey: ['campaign-full', campaignId],
    queryFn: async () => {
      try {
        // Fetch content from reports API which includes vodViews
        const contentResponse = await reportsAPI.getCampaignContent(campaignId, { limit: 1000 }).then(r => r.data);
        const creatorResponse = await reportsAPI.getCampaignCreators(campaignId).then(r => r.data);
        
        console.log('Content response:', contentResponse);
        console.log('Creator response:', creatorResponse);
        
        // Flatten metrics into content objects for transformCampaignToReportData
        const flattenedContent = contentResponse.content?.map(c => ({
          id: c.id,
          title: c.title,
          platform: c.platform,
          contentType: c.contentType,
          contentUrl: c.contentUrl,
          thumbnailUrl: c.thumbnailUrl,
          postDate: c.postDate,
          creatorId: c.creator?.id,
          creator: c.creator,
          segments: c.segments || [],
          isPaid: c.isPaid || false,
          // Flatten metrics into top level. Kick VOD views are typed in by hand
          // (no VOD API), so they may exist while `views` is still 0 — mirror them.
          views: c.platform?.toLowerCase() === 'kick' ? (c.metrics?.views || c.metrics?.vodViews || 0) : (c.metrics?.views || 0),
          // For Twitch, vodViews = views. For other platforms, use actual vodViews
          vodViews: c.platform?.toLowerCase() === 'twitch' ? (c.metrics?.views || 0) : (c.metrics?.vodViews || 0),
          likes: c.metrics?.likes || 0,
          comments: c.metrics?.comments || 0,
          shares: c.metrics?.shares || 0,
          impressions: c.metrics?.impressions || 0,
          engagementRate: c.metrics?.engagementRate || 0,
          streamAvgCcv: c.metrics?.avgCcv || 0,
          streamPeakCcv: c.metrics?.peakCcv || 0,
          streamWatchHours: c.metrics?.watchHours || 0,
          streamDuration: c.metrics?.streamDuration || 0
        })) || [];
        
        // Map content to influencers by creator ID
        const reportInfluencers = creatorResponse.creators?.map(cr => ({
          creator: cr.creator,
          campaign: cr.campaign,
          metrics: cr.metrics,
          content: flattenedContent.filter(c => c.creatorId === cr.creator.id) || []
        })) || [];
        
        const transformed = transformCampaignToReportData({
          influencers: reportInfluencers
        });

        // Raw content for the Executive Summary roll-ups. These MUST match the
        // campaign dashboard exactly (campaign = source of truth), so we keep the
        // untransformed metric fields (real impressions, real vodViews — no
        // estimates, no Twitch views/vodViews swap) and aggregate them via the
        // shared aggregateContentMetrics() using the campaign's own formulas.
        transformed.metricsContent = (contentResponse.content || []).map(c => ({
          id: c.id,
          platform: c.platform,
          contentType: c.contentType,
          // postDate is REQUIRED for impressionsFor() to apply the post-2026-08-24
          // YouTube 5x rule — without it every item defaults to 10x, so the
          // Impressions card read exactly views×10.
          postDate: c.postDate,
          isPaid: c.isPaid || false,
          views: c.metrics?.views || 0,
          vodViews: c.metrics?.vodViews || 0,
          impressions: c.metrics?.impressions || 0,
          likes: c.metrics?.likes || 0,
          comments: c.metrics?.comments || 0,
          shares: c.metrics?.shares || 0,
          avgCcv: c.metrics?.avgCcv || 0,
          peakCcv: c.metrics?.peakCcv || 0,
          // Game-segment watch hours, matching the campaign dashboard.
          watchHours: c.metrics?.gameWatchHours || 0,
        }));

        console.log('Transformed data:', transformed);
        return transformed;
      } catch (error) {
        console.error('Error fetching from reports API, falling back to campaign API:', error);
        // Fallback to campaign API if reports API fails
        const response = await campaignsAPI.getFullData(campaignId);
        const campaign = response.data?.campaign || response.data;
        
        // Ensure vodViews is properly set in fallback data
        if (campaign && campaign.influencers) {
          campaign.influencers = campaign.influencers.map(influencer => ({
            ...influencer,
            content: (influencer.content || []).map(content => ({
              ...content,
              // For Twitch, vodViews = views. For other platforms, use vodViews.
              // Kick: hand-entered vodViews also stand in for views (no VOD API).
              views: content.platform?.toLowerCase() === 'kick'
                ? (content.views || content.vodViews || 0)
                : content.views,
              vodViews: content.platform?.toLowerCase() === 'twitch'
                ? (content.views || 0)
                : (content.vodViews || content.vodViewCount || 0)
            }))
          }));
        }
        
        const transformedFallback = transformCampaignToReportData(campaign);
        // Keep the Executive Summary roll-ups working on the fallback path too.
        transformedFallback.metricsContent = (campaign?.influencers || []).flatMap(inf =>
          (inf.content || []).map(c => ({
            id: c.id,
            platform: c.platform,
            contentType: c.contentType,
            // Required for the post-2026-08-24 YouTube 5x impressions rule (see above).
            postDate: c.postDate,
            isPaid: c.isPaid || false,
            views: c.views || 0,
            vodViews: c.vodViews || 0,
            impressions: c.impressions || 0,
            likes: c.likes || 0,
            comments: c.comments || 0,
            shares: c.shares || 0,
            avgCcv: c.streamAvgCcv || c.avgCcv || 0,
            peakCcv: c.streamPeakCcv || c.peakCcv || 0,
            watchHours: c.watchHours || c.streamWatchHours || c.watchTimeHours || 0,
          }))
        );
        return transformedFallback;
      }
    },
    enabled: !!campaignId
  });

  // Load existing report data into form
  useEffect(() => {
    if (existingReport) {
      console.log('=== LOADING EXISTING REPORT ===');
      console.log('existingReport.title:', existingReport.title);
      console.log('existingReport.contentBlocks:', existingReport.contentBlocks);
      console.log('Full existingReport:', existingReport);

      setFormData({
        title: existingReport.title || '',
        reportPeriodStart: existingReport.reportPeriodStart
          ? new Date(existingReport.reportPeriodStart).toISOString().split('T')[0]
          : '',
        reportPeriodEnd: existingReport.reportPeriodEnd
          ? new Date(existingReport.reportPeriodEnd).toISOString().split('T')[0]
          : '',
        activitiesCompleted: existingReport.activitiesCompleted || '',
        upcomingActivities: existingReport.upcomingActivities || '',
        overallAnalysis: existingReport.overallAnalysis || '',
        videoAnalysis: existingReport.videoAnalysis || '',
        streamingAnalysis: existingReport.streamingAnalysis || '',
        socialMediaAnalysis: existingReport.socialMediaAnalysis || '',
        status: existingReport.status || 'draft'
      });
      // Load content blocks if they exist — ONCE, so a later refetch can't wipe edits.
      if (existingReport.contentBlocks && !contentBlocksLoadedRef.current) {
        console.log('Setting contentBlocks from existingReport');
        setContentBlocks(existingReport.contentBlocks);
        contentBlocksLoadedRef.current = true;
      }
    }
  }, [existingReport]);

  // Load content blocks and branding from full report data (when editing)
  useEffect(() => {
    if (fullReportData) {
      console.log('=== LOADING FULL REPORT DATA ===');
      console.log('fullReportData.contentBlocks:', fullReportData.contentBlocks);
      console.log('fullReportData.branding:', fullReportData.branding);
      console.log('fullReportData.report.reportPeriodStart:', fullReportData.report?.reportPeriodStart);
      console.log('fullReportData.report.reportPeriodEnd:', fullReportData.report?.reportPeriodEnd);

      // Load content blocks from full report data — ONCE, so a later refetch
      // (window focus / post-save invalidation) can't wipe just-added images.
      if (fullReportData.contentBlocks && Array.isArray(fullReportData.contentBlocks) && !contentBlocksLoadedRef.current) {
        console.log('Setting contentBlocks from fullReportData:', fullReportData.contentBlocks);
        setContentBlocks(fullReportData.contentBlocks);
        contentBlocksLoadedRef.current = true;
      }
      // Load branding from full report data - only on initial load
      // Skip if branding was already loaded or user has saved new branding
      if (fullReportData.branding && !brandingLoadedRef.current) {
        setGameBranding(fullReportData.branding);
        brandingLoadedRef.current = true; // Mark as loaded to prevent refetch/overwrite
        // Also set gameId from branding
        if (fullReportData.branding.gameId) {
          setGameId(fullReportData.branding.gameId);
        }
      } else if (fullReportData.branding?.gameId && !gameId) {
        // Still set gameId if not already set (needed for save functionality)
        setGameId(fullReportData.branding.gameId);
      }
      // Load excluded/highlighted content IDs from the report ONCE. After that,
      // the user's in-session restore/exclude actions (which also persist to the
      // server) are authoritative — a later refetch must not overwrite them.
      if (!exclusionsLoadedRef.current) {
        if (fullReportData.report?.excludedContentIds) {
          setExcludedContentIds(fullReportData.report.excludedContentIds);
        }
        if (fullReportData.report?.highlightedVideoIds) {
          setHighlightedVideoIds(fullReportData.report.highlightedVideoIds);
        }
        if (fullReportData.report?.highlightedStreamIds) {
          setHighlightedStreamIds(fullReportData.report.highlightedStreamIds);
        }
        exclusionsLoadedRef.current = true;
      }
    }
  }, [fullReportData]);

  // Auto-refresh data if report has saved date filters
  useEffect(() => {
    if (fullReportData?.report?.reportPeriodStart || fullReportData?.report?.reportPeriodEnd) {
      console.log('✅ Report has saved date filters, auto-refreshing data...');
      console.log('reportId:', reportId);
      console.log('reportPeriodStart:', fullReportData.report.reportPeriodStart);
      console.log('reportPeriodEnd:', fullReportData.report.reportPeriodEnd);
      
      // Use a small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        if (!reportId) {
          console.warn('❌ Cannot refresh - no reportId provided');
          return;
        }
        
        setIsRefreshingData(true);
        clientReportsAPI.refreshReportData(
          reportId,
          fullReportData.report.reportPeriodStart,
          fullReportData.report.reportPeriodEnd
        ).then(response => {
          console.log('✅ Auto-refresh successful, updating state');
          setRefreshedReportData(response.data);
        }).catch(error => {
          console.error('❌ Error auto-refreshing report data:', error);
        }).finally(() => {
          setIsRefreshingData(false);
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [fullReportData?.report?.reportPeriodStart, fullReportData?.report?.reportPeriodEnd, reportId]);

  // Fetch game branding when campaign data is available (for new reports)
  useEffect(() => {
    const fetchBranding = async () => {
      // Only fetch if we haven't already loaded branding (prevents refetch after save)
      if (brandingLoadedRef.current) return;

      if (campaignFullData?.campaign?.game) {
        try {
          // Need to get the game ID from the campaign
          const campaignRes = await campaignsAPI.getOne(campaignId);
          const fetchedGameId = campaignRes.data?.campaign?.gameId || campaignRes.data?.gameId;
          if (fetchedGameId) {
            setGameId(fetchedGameId); // Store the gameId
            const brandingRes = await gamesAPI.getBranding(fetchedGameId);
            setGameBranding(brandingRes.data?.branding || null);
            brandingLoadedRef.current = true;
          }
        } catch (err) {
          console.log('No branding found for game');
        }
      }
    };
    fetchBranding();
  }, [campaignFullData, campaignId]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => clientReportsAPI.createReport(data),
    onSuccess: (response) => {
      setSaveStatus('saved');
      queryClient.invalidateQueries(['client-reports', campaignId]);
      setTimeout(() => setSaveStatus(null), 2000);
      const newId = response.data?.id;
      if (newId) {
        setCreatedReportId(newId);
        window.history.replaceState(null, '', `/client-report/${campaignId}/${newId}`);
      }
    },
    onError: () => {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => clientReportsAPI.updateReport(reportId, data),
    onSuccess: (response) => {
      console.log('=== UPDATE SUCCESS ===');
      console.log('Response data:', response.data);
      console.log('Saved title:', response.data?.title);
      console.log('Saved contentBlocks:', response.data?.contentBlocks);
      setSaveStatus('saved');
      queryClient.invalidateQueries(['client-report', reportId]);
      queryClient.invalidateQueries(['client-report-full', reportId]);
      queryClient.invalidateQueries(['client-reports']);
      refetchFullData();
      setTimeout(() => setSaveStatus(null), 2000);
    },
    onError: (error) => {
      console.error('=== UPDATE ERROR ===');
      console.error('Error:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  });

  // State to hold refreshed report data when dates change
  const [refreshedReportData, setRefreshedReportData] = useState(null);
  const [isRefreshingData, setIsRefreshingData] = useState(false);

  const handleInputChange = (field, value) => {
    // Update form data first
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Refresh the stats only once BOTH dates are set (avoids refreshing on the
    // start date alone).
    if (field === 'reportPeriodStart' || field === 'reportPeriodEnd') {
      const bothDates = newFormData.reportPeriodStart && newFormData.reportPeriodEnd;
      if (!bothDates) return;
      if (isEditing) {
        // Existing report: refresh directly against its id.
        handleRefreshReportDataWithDates(newFormData.reportPeriodStart, newFormData.reportPeriodEnd);
      } else if (!createMutation.isLoading && !createdReportId) {
        // NEW report: no id yet to refresh against. Auto-create ONE draft with these
        // dates (guarded so it never spawns duplicates) — once created, reportId
        // exists and the saved-dates auto-refresh effect updates the stats.
        createMutation.mutate({
          ...newFormData,
          campaignId,
          status: newFormData.status || 'draft',
          contentBlocks: contentBlocks.length > 0 ? contentBlocks : null,
          highlightedVideoIds: highlightedVideoIds.length > 0 ? highlightedVideoIds : [],
          highlightedStreamIds: highlightedStreamIds.length > 0 ? highlightedStreamIds : []
        });
      }
    }
  };

  // Refresh report data based on new date range
  const handleRefreshReportDataWithDates = async (startDate, endDate) => {
    if (!reportId) {
      console.warn('❌ Cannot refresh - no reportId provided');
      return;
    }
    
    console.log('\n=== STARTING DATA REFRESH ===');
    console.log('Report ID:', reportId);
    console.log('Start Date:', startDate || '(empty)');
    console.log('End Date:', endDate || '(empty)');
    
    setIsRefreshingData(true);
    try {
      console.log('Making API call to refresh-data endpoint...');
      const response = await clientReportsAPI.refreshReportData(reportId, startDate, endDate);
      console.log('✅ API Response received successfully');
      console.log('Response status:', response.status);
      console.log('Response data keys:', Object.keys(response.data));
      console.log('Response data summary:', {
        summary: response.data.summary ? 'present' : 'missing',
        videoSection: response.data.videoSection ? 'present' : 'missing',
        streamingSection: response.data.streamingSection ? 'present' : 'missing',
        platforms: Array.isArray(response.data.platforms) ? `${response.data.platforms.length} platforms` : 'not array',
        creators: Array.isArray(response.data.creators) ? `${response.data.creators.length} creators` : 'not array'
      });
      
      setRefreshedReportData(response.data);
      console.log('✅ refreshedReportData state updated');
      console.log('=== REFRESH COMPLETE ===\n');
    } catch (error) {
      console.error('❌ Error refreshing report data:');
      console.error('Error message:', error.message);
      console.error('Error response:', error.response?.data);
      console.error('Full error:', error);
      alert('Failed to refresh report data: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsRefreshingData(false);
    }
  };

  const handleSave = async (status = formData.status) => {
    setSaveStatus('saving');

    const data = {
      ...formData,
      campaignId,
      status,
      contentBlocks: contentBlocks.length > 0 ? contentBlocks : null,
      highlightedVideoIds: highlightedVideoIds.length > 0 ? highlightedVideoIds : [],
      highlightedStreamIds: highlightedStreamIds.length > 0 ? highlightedStreamIds : []
    };

    console.log('=== SAVING REPORT ===');
    console.log('Title:', data.title);
    console.log('ContentBlocks:', data.contentBlocks);
    console.log('Full data:', data);

    if (isEditing) {
      updateMutation.mutate(data);
    } else if (!createMutation.isLoading) {
      createMutation.mutate(data);
    }
  };

  // Image block handlers
  const handleAddImageBlock = (position) => {
    setImageInsertPosition(position);
    setShowImagePicker(true);
  };

  const handleImageSelected = (imageBlock) => {
    const newBlock = {
      ...imageBlock,
      id: Date.now().toString(),
      position: imageInsertPosition
    };
    contentBlocksLoadedRef.current = true; // local edits are now authoritative — don't let a refetch overwrite them
    setContentBlocks(prev => [...prev, newBlock]);
    setShowImagePicker(false);
    setImageInsertPosition(null);
  };

  const handleDeleteImageBlock = (blockId) => {
    contentBlocksLoadedRef.current = true;
    setContentBlocks(prev => prev.filter(b => b.id !== blockId));
  };

  // Get blocks for a specific position
  const getBlocksForPosition = (position) => {
    return contentBlocks.filter(b => b.position === position);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Use either refreshed data, full report data, or campaign data
  // refreshedReportData takes precedence when dates change
  const reportData = refreshedReportData ? refreshedReportData : (campaignFullData ? {
    ...campaignFullData,
    // Keep timeline data from existing report if available
    videoSection: {
      ...campaignFullData.videoSection,
      timeline: fullReportData?.videoSection?.timeline || campaignFullData.videoSection?.timeline || []
    },
    streamingSection: {
      ...campaignFullData.streamingSection,
      timeline: fullReportData?.streamingSection?.timeline || campaignFullData.streamingSection?.timeline || []
    }
  } : fullReportData);
  
  // Add safety fallbacks to ensure all properties exist
  const safeReportData = reportData || {
    campaign: {},
    summary: { reach: 0, views: 0, totalEngagement: 0, engagementRate: 0, avgCcv: 0, watchHours: 0 },
    videoSection: { metrics: { count: 0, totalViews: 0, totalLikes: 0, totalComments: 0, totalShares: 0, engagementRate: 0 }, timeline: [], content: [] },
    streamingSection: { metrics: { count: 0, averageCcv: 0, peakCcvMax: 0, totalWatchHours: 0, totalImpressions: 0, totalVodViews: 0 }, timeline: [], content: [] },
    platforms: [],
    creators: []
  };

  // Compute adjusted metrics based on excluded content
  const adjustedMetrics = useMemo(() => {
    const videoContent = (safeReportData.videoSection?.content || []).filter(c => !excludedContentIds.includes(c.id));
    const streamContent = (safeReportData.streamingSection?.content || []).filter(c => !excludedContentIds.includes(c.id));
    const socialMediaContent = (safeReportData.socialMediaSection?.content || []).filter(c => !excludedContentIds.includes(c.id));

    // Shared view-style metric calculator used by both videos and social media
    const computeViewLikeMetrics = (rows) => ({
      count: rows.length,
      totalViews: rows.reduce((sum, c) => sum + (c.views || 0), 0),
      totalLikes: rows.reduce((sum, c) => sum + (c.likes || 0), 0),
      totalComments: rows.reduce((sum, c) => sum + (c.comments || 0), 0),
      totalShares: rows.reduce((sum, c) => sum + (c.shares || 0), 0),
      engagementRate: rows.length > 0
        ? rows.reduce((sum, c) => {
            const engagement = (c.likes || 0) + (c.comments || 0) + (c.shares || 0);
            const rate = c.views > 0 ? (engagement / c.views) * 100 : 0;
            return sum + rate;
          }, 0) / rows.length
        : 0
    });

    const videoMetrics = computeViewLikeMetrics(videoContent);
    const socialMediaMetrics = computeViewLikeMetrics(socialMediaContent);

    // Streaming metrics
    const streamMetrics = {
      count: streamContent.length,
      averageCcv: Math.round(streamContent.reduce((sum, s) => sum + (s.avgCcv || s.streamAvgCcv || 0), 0)),
      peakCcvMax: Math.round(streamContent.reduce((sum, s) => sum + (s.peakCcv || s.streamPeakCcv || 0), 0)),
      totalWatchHours: streamContent.reduce((sum, s) => sum + (s.watchHours || s.streamWatchHours || s.watchTimeHours || 0), 0),
      totalVodViews: streamContent.reduce((sum, s) => sum + (s.vodViews || 0), 0),
      totalImpressions: streamContent.reduce((sum, s) => sum + (s.impressions || 0), 0)
    };

    // Summary metrics
    const allContent = [...videoContent, ...streamContent, ...socialMediaContent];
    const totalViews = videoMetrics.totalViews + streamMetrics.totalVodViews + socialMediaMetrics.totalViews;
    const totalLikes = videoMetrics.totalLikes + socialMediaMetrics.totalLikes;
    // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is estimated at
    // 5x rather than 10x, so a single multiplier over a view total is wrong.
    const videoImpressions = videoContent.reduce((sum, c) => sum + impressionsFor(c, c.views || 0, c.impressions), 0);
    const streamImpressions = streamContent.reduce((sum, s) => sum + impressionsFor(s, s.vodViews || 0, s.impressions), 0);
    const socialMediaImpressions = socialMediaContent.reduce((sum, c) => sum + impressionsFor(c, c.views || 0, c.impressions), 0);
    const totalEstimatedImpressions = videoImpressions + streamImpressions + socialMediaImpressions;
    const summary = {
      totalEstimatedImpressions,
      views: totalViews,
      totalLikes,
      engagementRate: totalViews > 0 ? (totalLikes / totalViews) * 100 : 0,
      avgCcv: streamMetrics.averageCcv,
      watchHours: streamMetrics.totalWatchHours
    };

    return { videoMetrics, streamMetrics, socialMediaMetrics, summary };
  }, [safeReportData.videoSection?.content, safeReportData.streamingSection?.content, safeReportData.socialMediaSection?.content, excludedContentIds]);

  const handleExportPDF = async () => {
    if (!safeReportData) return;

    try {
      // Include all report data needed for PDF generation
      const pdfData = {
        ...safeReportData,
        // Include the report form data with analysis text
        report: {
          ...formData,
          reportPeriodStart: formData.reportPeriodStart,
          reportPeriodEnd: formData.reportPeriodEnd,
          activitiesCompleted: formData.activitiesCompleted,
          upcomingActivities: formData.upcomingActivities,
          overallAnalysis: formData.overallAnalysis,
          videoAnalysis: formData.videoAnalysis,
          streamingAnalysis: formData.streamingAnalysis,
          socialMediaAnalysis: formData.socialMediaAnalysis,
        },
        // Include branding settings
        branding: gameBranding,
        // Include custom image blocks
        contentBlocks,
        // Metric visibility and exclusions
        metricVisibility,
        excludedContentIds,
        // Use adjusted metrics for summary
        summary: adjustedMetrics.summary,
        // Pass sorting options so PDF reflects user's chosen sort order
        videoSortBy,
        streamingSortBy,
        socialMediaSortBy
      };

      // Use server-side PDF generation for better quality
      const response = await reportsAPI.generatePDF(pdfData);

      // Create download link from blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename — "<report title> - <game>" so multiple reports for
      // the same campaign download with distinct, readable names. Strips only
      // filesystem-illegal characters; falls back to campaign name + date when
      // the report has no title yet.
      const clean = (s) => (s || '').replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').trim();
      const rawGame = pdfData.campaign?.game;
      const gameName = clean(typeof rawGame === 'object' ? (rawGame?.title || rawGame?.name) : rawGame);
      const titlePart = clean(formData.title);
      let downloadName;
      if (titlePart) {
        downloadName = `${gameName ? `${titlePart} - ${gameName}` : titlePart}.pdf`;
      } else {
        const campaignName = clean(pdfData.campaign?.name) || 'report';
        const dateStr = new Date().toISOString().split('T')[0];
        downloadName = `${campaignName} - ${dateStr}.pdf`;
      }
      link.download = downloadName;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (reportLoading || campaignLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-sc-pink" />
        <span className="ml-2 text-white/60">Loading {isEditing ? 'report' : 'campaign'} data...</span>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={(() => {
        const s = { backgroundColor: '#0a0a1a' };
        let bgUrl = fullReportData?.campaign?.gameImage || reportData?.campaign?.gameImage;
        if (bgUrl) {
          if (bgUrl.includes('images.igdb.com') && bgUrl.includes('/t_')) {
            bgUrl = bgUrl.replace(/\/t_[^/]+\//, '/t_720p/');
          }
          s.backgroundImage = `url(${bgUrl})`;
          s.backgroundSize = 'cover';
          s.backgroundPosition = 'center';
          s.backgroundAttachment = 'fixed';
          s.backgroundRepeat = 'no-repeat';
        }
        return s;
      })()}
    >
      {/* Dark overlay — matches portal gradient */}
      <div className="fixed inset-0" style={{
        background: 'linear-gradient(180deg, rgba(10,10,26,0.65) 0%, rgba(10,10,26,0.82) 35%, rgba(10,10,26,0.92) 65%, rgba(10,10,26,0.97) 100%)',
        zIndex: 0,
      }} />
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(10,10,26,0.9)', borderBottom: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onBack}
                className="mr-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white/60" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">
                  {isEditing ? 'Edit Client Report' : 'Create Client Report'}
                </h1>
                <p className="text-sm text-white/50">
                  {reportData?.campaign?.name || 'Campaign Report'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Save Status */}
              {saveStatus && (
                <div className={`flex items-center px-3 py-1.5 rounded-lg text-sm ${
                  saveStatus === 'saving' ? 'bg-white/10 text-white/60' :
                  saveStatus === 'saved' ? 'bg-green-500/20 text-green-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {saveStatus === 'saving' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {saveStatus === 'saved' && <CheckCircle className="w-4 h-4 mr-2" />}
                  {saveStatus === 'error' && <AlertCircle className="w-4 h-4 mr-2" />}
                  {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Error saving'}
                </div>
              )}

              {/* Branding Button */}
              <button
                onClick={() => setShowBrandingEditor(!showBrandingEditor)}
                className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showBrandingEditor
                    ? 'bg-purple-500/20 text-purple-400'
                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                <Palette className="w-4 h-4 mr-2" />
                Branding
              </button>

              {/* Full Preview Button */}
              <button
                onClick={() => setShowFullPreview(true)}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
              >
                <Maximize2 className="w-4 h-4 mr-2" />
                Full Preview
              </button>

              {/* Preview Button */}
              {isEditing && (
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showPreview
                      ? 'bg-sc-pink/20 text-sc-pink'
                      : 'bg-white/5 text-white/70 hover:bg-white/10'
                  }`}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Edit Mode' : 'Preview'}
                </button>
              )}

              {/* Save Draft */}
              <button
                onClick={() => handleSave('draft')}
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)' }}
              >
                <Save className="w-4 h-4 mr-2" />
                {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save Draft'}
              </button>
              
              {/* Download Report */}
              {isEditing && (
                <button
                  onClick={handleExportPDF}
                  className="inline-flex items-center px-4 py-2 bg-sc-pink text-white rounded-lg text-sm font-medium hover:bg-sc-pink/90 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-[1]">
        {/* Branding Editor Panel - Positioned at top for visibility */}
        {showBrandingEditor && (
          <div className="mb-6">
            <GameBrandingEditor
              gameId={gameId}
              gameName={campaignFullData?.campaign?.game}
              initialBranding={gameBranding}
              onSave={(branding) => {
                setGameBranding(branding);
              }}
            />
          </div>
        )}

        {/* Excluded Content Dropdown - only shows when items are excluded */}
        {excludedContentIds.length > 0 && (
          <div className="mb-4 relative">
            <button
              onClick={() => setShowExcludedDropdown(!showExcludedDropdown)}
              className="w-full rounded-lg px-4 py-3 flex items-center justify-between hover:brightness-125 transition-colors"
              style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.3)' }}
            >
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-400">
                  {excludedContentIds.length} content item{excludedContentIds.length !== 1 ? 's' : ''} excluded from report
                </span>
              </div>
              <ChevronDown className={`w-4 h-4 text-orange-400 transition-transform ${showExcludedDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showExcludedDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto bg-gray-900 border border-gray-700">
                <div className="p-3 flex items-center justify-between bg-white/5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <span className="text-xs font-medium text-white/50">Click to restore content to report</span>
                  <button
                    onClick={async () => {
                      for (const contentId of excludedContentIds) {
                        try {
                          await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/restore-content`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ contentId })
                          });
                        } catch (error) {
                          console.error('Error restoring content:', error);
                        }
                      }
                      setExcludedContentIds([]);
                      setShowExcludedDropdown(false);
                    }}
                    className="text-xs text-orange-400 hover:text-orange-300 font-medium"
                  >
                    Restore All
                  </button>
                </div>
                <div className="divide-y divide-white/5">
                  {[
                    ...(safeReportData.videoSection?.content || []),
                    ...(safeReportData.streamingSection?.content || [])
                  ]
                    .filter(c => excludedContentIds.includes(c.id))
                    .map(content => (
                      <div
                        key={content.id}
                        className="p-3 hover:bg-white/5 flex items-center gap-3 cursor-pointer"
                        onClick={async () => {
                          try {
                            const response = await fetch(`${API_BASE_URL}/api/client-reports/${reportId}/restore-content`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ contentId: content.id })
                            });
                            if (response.ok) {
                              const data = await response.json();
                              setExcludedContentIds(data.excludedContentIds || []);
                              if (data.excludedContentIds?.length === 0) {
                                setShowExcludedDropdown(false);
                              }
                            }
                          } catch (error) {
                            console.error('Error restoring content:', error);
                          }
                        }}
                      >
                        {content.thumbnailUrl ? (
                          <img
                            src={resolveThumbnailUrl(content.thumbnailUrl)}
                            alt={content.title}
                            className="w-12 h-9 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-9 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                            <Video className="w-5 h-5 text-white/40" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{content.title || 'Untitled'}</p>
                          <p className="text-xs text-white/50">{content.platform} • {content.creator?.name}</p>
                        </div>
                        <RotateCcw className="w-4 h-4 text-orange-500 flex-shrink-0" />
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Header Banner Image Block */}
        <div className="mb-6">
          {getBlocksForPosition('header').length > 0 ? (
            <div className="rounded-2xl overflow-hidden bg-gray-900 border border-gray-700 shadow-lg">
              {getBlocksForPosition('header').map((block) => (
                <div key={block.id} className="relative group">
                  <img
                    src={block.src}
                    alt={block.caption || 'Header banner'}
                    className="w-full h-48 object-cover"
                  />
                  {block.caption && (
                    <p className="text-center text-sm text-white/50 italic py-2 bg-white/5">{block.caption}</p>
                  )}
                  <button
                    onClick={() => handleDeleteImageBlock(block.id)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-50 rounded text-xs text-white">
                    Header Banner
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <button
              onClick={() => handleAddImageBlock('header')}
              className="w-full p-6 border-2 border-dashed rounded-xl hover:border-sc-pink hover:bg-sc-pink/10 transition-colors flex items-center justify-center space-x-3"
              style={{ borderColor: 'rgba(255,255,255,0.15)' }}
            >
              <ImageIcon className="w-6 h-6 text-white/40" />
              <span className="text-sm font-medium text-white/50">Add Header Banner Image</span>
            </button>
          )}
        </div>

        {/* Report Title & Period */}
        <div className="rounded-2xl p-6 mb-6 relative overflow-hidden bg-gray-900 border border-gray-700 shadow-lg">
          {/* Accent glow */}
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#fe2388' }} />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#3b82f6' }} />
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center relative z-10">
            <Calendar className="w-5 h-5 mr-2 text-sc-pink" />
            Report Details
          </h3>

          {/* Report Title */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-white/70 mb-1">
              Report Title <span className="text-white/40 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder={campaignFullData?.campaign?.name || 'Campaign Report'}
              className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink text-white placeholder-white/30"
              style={DARK_INPUT}
            />
            <p className="text-xs text-white/40 mt-1">Leave blank to use campaign name as title</p>
          </div>

          {/* Report Period */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={formData.reportPeriodStart}
                onChange={(e) => handleInputChange('reportPeriodStart', e.target.value)}
                className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink text-white [color-scheme:dark]"
                style={DARK_INPUT}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-white/70 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={formData.reportPeriodEnd}
                onChange={(e) => handleInputChange('reportPeriodEnd', e.target.value)}
                className="w-full px-4 py-2 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink text-white [color-scheme:dark]"
                style={DARK_INPUT}
              />
            </div>
          </div>

          {/* Data Refresh Status */}
          {isEditing && isRefreshingData && (
            <div className="mt-4 p-3 bg-sc-pink/10 border border-sc-pink/30 rounded-lg flex items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin text-sc-pink" />
              <span className="text-sm text-sc-pink">Updating report data with new date range...</span>
            </div>
          )}
          {isEditing && refreshedReportData && !isRefreshingData && (
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center">
              <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
              <span className="text-sm text-green-400">Report data updated successfully!</span>
            </div>
          )}
          {existingReport?.updatedAt && (
            <p className="mt-3 text-xs text-white/25">
              YouTube &amp; Instagram data last refreshed: {new Date(existingReport.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Executive Summary Section */}
        <div className="rounded-2xl mb-6 overflow-hidden relative bg-gray-900 border border-gray-700 shadow-lg">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#fe2388' }} />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#8b5cf6' }} />
          <button
            onClick={() => toggleSection('executive')}
            className="w-full px-6 py-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors relative z-10"
          >
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FileText className="w-5 h-5 mr-2 text-sc-pink" />
              Executive Summary
            </h3>
            {expandedSections.executive ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>
          
          {expandedSections.executive && (
            <div className="p-6 space-y-6 relative z-10">
              {/* Hero KPI Cards — split into Organic + Sponsored blocks */}
              {reportData && (() => {
                const excl = (arr) => (arr || []).filter(c => !excludedContentIds.includes(c.id));

                const fmtP = (p) => ({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', twitter: 'Twitter', instagram: 'Instagram' }[p?.toLowerCase()] || p);
                const platformAccents = { twitch: '#9146ff', kick: '#00e70a', youtube: '#ff0000', tiktok: '#00f2ea', twitter: '#1da1f2', instagram: '#e1306c' };

                // Compute hero + secondary cards for a content subset (Organic or
                // Sponsored). Numbers come from aggregateContentMetrics() — the SAME
                // formulas the campaign dashboard uses — so the report matches the
                // campaign exactly. `subset` is the raw metricsContent for that split.
                const buildCards = (subset) => {
                  const m = aggregateContentMetrics(subset);
                  const heroKpis = [
                    { key: 'totalEstimatedImpressions', label: 'Total Combined Views', value: formatFullNumber(m.views), accent: '#e91e8a', tooltip: null },
                    { key: 'totalViews', label: 'Impressions', value: formatFullNumber(m.impressions), accent: '#f59e0b', tooltip: 'impressions' },
                    { key: 'totalLikes', label: 'Engagement', value: formatFullNumber(m.engagement), accent: '#22c55e', tooltip: 'engagement' },
                    { key: 'watchHours', label: 'Watch Hours', value: formatWatchHours(m.watchHours), accent: '#3b82f6', tooltip: 'watchHours' },
                  ];
                  const secondaryStats = [
                    { label: 'Content', value: m.count, accent: '#06b6d4' },
                    { key: 'avgCcv', label: 'Combined CCV', value: m.avgCcv, tooltip: 'watchHours', accent: '#a855f7' },
                    { key: 'engagementRate', label: 'Combined Peak CCV', value: m.peakCcv, accent: '#f97316' },
                    m.vodViews > 0 && { label: 'VOD Views', value: m.vodViews, accent: '#14b8a6' },
                    ...Object.entries(m.platViews).filter(([, v]) => v > 0).map(([p, v]) => ({ label: `${fmtP(p)} Views`, value: v, accent: platformAccents[p] || '#8b5cf6' })),
                  ].filter(Boolean);
                  return { heroKpis, secondaryStats, count: m.count };
                };

                const renderCards = ({ heroKpis, secondaryStats }) => (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      {heroKpis.map((kpi) => {
                        const visible = metricVisibility[kpi.key];
                        return (
                          <div key={kpi.key} onClick={() => toggleMetricVisibility(kpi.key)} className={`relative rounded-xl p-5 cursor-pointer transition-all overflow-hidden border ${visible ? 'bg-gray-900 border-gray-700 shadow-lg' : 'bg-white/5 border-white/10 opacity-50'}`}>
                            <div className={`absolute top-2 right-2 w-4 h-4 rounded-full border-2 flex items-center justify-center z-10 ${visible ? 'bg-sc-pink border-sc-pink' : 'bg-white/10 border-white/30'}`}>
                              {visible && <CheckCircle className="w-3 h-3 text-white" />}
                            </div>
                            {visible && <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: kpi.accent }} />}
                            <div className="relative z-10">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className={`text-xs font-medium ${visible ? 'text-white/60' : 'text-gray-500'}`}>{kpi.label}</span>
                                {kpi.tooltip && <YouTubeEstimateTooltip metric={kpi.tooltip} />}
                              </div>
                              <div className={`text-3xl font-bold mt-2 ${visible ? 'text-white' : 'text-gray-600'}`}>{kpi.value}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-2">
                      {secondaryStats.map((stat, i) => {
                        const visible = stat.key ? metricVisibility[stat.key] : true;
                        return (
                          <div key={i} onClick={stat.key ? () => toggleMetricVisibility(stat.key) : undefined} className={`relative rounded-xl p-4 overflow-hidden transition-all border ${stat.key ? 'cursor-pointer' : ''} ${visible ? 'bg-gray-900 border-gray-700 shadow-lg' : 'bg-white/5 border-white/10 opacity-50'}`}>
                            {stat.key && (
                              <div className={`absolute top-2 right-2 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center z-10 ${visible ? 'bg-sc-pink border-sc-pink' : 'bg-white/10 border-white/30'}`}>
                                {visible && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                              </div>
                            )}
                            {visible && <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: stat.accent }} />}
                            <div className="relative z-10">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className={`text-xs font-medium ${visible ? 'text-white/60' : 'text-gray-500'}`}>{stat.label}</span>
                                {stat.tooltip && <YouTubeEstimateTooltip metric={stat.tooltip} />}
                              </div>
                              <div className={`text-xl font-bold mt-1 ${visible ? 'text-white' : 'text-gray-600'}`}>{typeof stat.value === 'number' ? formatFullNumber(stat.value) : stat.value}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );

                // Roll-ups come from the raw metricsContent (campaign-matching),
                // not the transformed section items, so numbers equal the campaign.
                const metricsAll = excl(safeReportData.metricsContent);
                const organic = buildCards(metricsAll.filter(c => !c.isPaid));
                const sponsored = buildCards(metricsAll.filter(c => c.isPaid));

                return (
                  <>
                    {organic.count > 0 && (
                      <div className="mb-5">
                        <div className="text-sm font-semibold text-purple-300 mb-2">Organic Results</div>
                        {renderCards(organic)}
                      </div>
                    )}
                    {sponsored.count > 0 && (
                      <div className="mb-2">
                        <div className="text-sm font-semibold text-sc-pink mb-2">Sponsored Results</div>
                        {renderCards(sponsored)}
                      </div>
                    )}
                    {organic.count === 0 && sponsored.count === 0 && (
                      <p className="text-sm text-white/40">No content in this report yet.</p>
                    )}
                  </>
                );
              })()}
              <p className="text-xs text-white/40 mb-4">Click metrics to toggle visibility in PDF export</p>

              {/* Overall Analysis */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Overall Campaign Analysis
                </label>
                <textarea
                  value={formData.overallAnalysis}
                  onChange={(e) => handleInputChange('overallAnalysis', e.target.value)}
                  placeholder="Provide an overall analysis of campaign performance, key insights, and recommendations..."
                  rows={6}
                  className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink resize-none text-white placeholder-white/30"
                  style={DARK_INPUT}
                />
              </div>
            </div>
          )}
        </div>

        {/* Platform Breakdown Section */}
        <div className="rounded-2xl mb-6 overflow-hidden relative bg-gray-900 border border-gray-700 shadow-lg">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#3b82f6' }} />
          <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#22c55e' }} />
          <button
            onClick={() => toggleSection('platforms')}
            className="w-full px-6 py-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors relative z-10"
          >
            <h3 className="text-lg font-semibold text-white flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-sc-pink" />
              Platform Breakdown
            </h3>
            {expandedSections.platforms ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>

          {expandedSections.platforms && reportData?.platforms && (() => {
            const fmtPlatform = (p) => ({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', twitter: 'Twitter', instagram: 'Instagram' }[p?.toLowerCase()] || p);
            // Recalculate platform data from filtered content (respecting excludedContentIds)
            const filteredAllContent = [
              ...(safeReportData.videoSection?.content || []).filter(c => !excludedContentIds.includes(c.id)),
              ...(safeReportData.streamingSection?.content || []).filter(c => !excludedContentIds.includes(c.id)),
              ...(safeReportData.socialMediaSection?.content || []).filter(c => !excludedContentIds.includes(c.id)),
            ];
            const platformMap = new Map();
            const platformCreatorSets = new Map();
            for (const c of filteredAllContent) {
              const plat = c.platform || 'Unknown';
              if (!platformMap.has(plat)) {
                platformMap.set(plat, { platform: plat, creatorCount: 0, contentCount: 0, views: 0, likes: 0, comments: 0, shares: 0 });
                platformCreatorSets.set(plat, new Set());
              }
              const p = platformMap.get(plat);
              p.contentCount++;
              p.views += Number(c.views) || Number(c.vodViews) || 0;
              p.likes += Number(c.likes) || 0;
              p.comments += Number(c.comments) || 0;
              p.shares += Number(c.shares) || 0;
              const cName = c.creatorName || c.creator?.name;
              if (cName && !platformCreatorSets.get(plat).has(cName)) {
                platformCreatorSets.get(plat).add(cName);
                p.creatorCount++;
              }
            }
            // Preserve platform order from original data, but use filtered counts
            // Also include platforms that exist in content but not in the server's platform list (e.g., Instagram)
            const rawPlatforms = safeReportData.platforms || [];
            const rawPlatformNames = new Set(rawPlatforms.map(rp => rp.platform));
            const platforms = [
              ...rawPlatforms.map(rp => platformMap.get(rp.platform) || { ...rp, contentCount: 0, views: 0, likes: 0, comments: 0, shares: 0 }),
              ...[...platformMap.values()].filter(p => !rawPlatformNames.has(p.platform))
            ];
            const totalViews = platforms.reduce((sum, p) => sum + (p.views || 0), 0);

            // Build donut chart data
            const platformSplitData = platforms.map(p => ({
              name: fmtPlatform(p.platform),
              value: p.views || 0,
              color: getPlatformColor(p.platform),
              percentage: totalViews > 0 ? ((p.views / totalViews) * 100).toFixed(1) : '0',
            }));

            // Creator coverage - all time (uses totalContentCount which is not date-filtered)
            const creators = safeReportData.creators || [];
            const covered = creators.filter(c => (c.metrics?.totalContentCount || c.metrics?.contentCount || 0) > 0).length;
            const pending = creators.length - covered;
            const coverageTotal = covered + pending;
            const coverageData = [
              { name: 'Covered', value: covered, color: '#22c55e', percentage: coverageTotal > 0 ? ((covered / coverageTotal) * 100).toFixed(0) : 0 },
              { name: 'Pending', value: pending, color: '#4b5563', percentage: coverageTotal > 0 ? ((pending / coverageTotal) * 100).toFixed(0) : 0 },
            ];

            // Region breakdown - build lookup from creators, count from content in period
            // (follows same pattern as client portal buildRegionData)
            const creatorRegionLookup = {};
            for (const c of creators) {
              if (c.creator?.id) {
                creatorRegionLookup[c.creator.id] = c.creator.region || c.creator.country || 'Unknown';
              }
            }
            const allContent = filteredAllContent;
            const regionCounts = {};
            const seenCreators = new Set();
            for (const c of allContent) {
              const creatorId = c.creatorId || c.creator?.id;
              const cName = c.creatorName || c.creator?.name;
              if ((!creatorId && !cName) || seenCreators.has(creatorId || cName)) continue;
              seenCreators.add(creatorId || cName);
              const region = (creatorId && creatorRegionLookup[creatorId]) || c.creatorRegion || c.creator?.region || c.creator?.country || 'Unknown';
              regionCounts[region] = (regionCounts[region] || 0) + 1;
            }
            const regionColors = ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
            const sortedRegions = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);
            const regionCreatorCount = seenCreators.size;
            const topRegions = sortedRegions.slice(0, 5);
            const otherCount = sortedRegions.slice(5).reduce((sum, [, count]) => sum + count, 0);
            const regionData = topRegions.map(([name, value], i) => ({
              name, value, color: regionColors[i % regionColors.length],
              percentage: regionCreatorCount > 0 ? ((value / regionCreatorCount) * 100).toFixed(0) : 0,
            }));
            if (otherCount > 0) regionData.push({ name: 'Other', value: otherCount, color: '#6b7280', percentage: regionCreatorCount > 0 ? ((otherCount / regionCreatorCount) * 100).toFixed(0) : 0 });

            const ReportDonutChart = ({ data, centerValue, centerLabel, title }) => (
              <div className="rounded-xl p-4 flex flex-col items-center bg-gray-900 border border-gray-700 shadow-lg">
                <h4 className="text-sm font-semibold text-white/70 mb-2">{title}</h4>
                <div className="relative" style={{ width: 180, height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={2} dataKey="value" stroke="none">
                        {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold text-white">{centerValue}</span>
                    <span className="text-xs text-white/50">{centerLabel}</span>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
                  {data.map((entry, i) => (
                    <div key={i} className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-white/60">{entry.name} ({entry.percentage}%)</span>
                    </div>
                  ))}
                </div>
              </div>
            );

            return (
              <div className="p-6 space-y-6 relative z-10">
                {/* Platform Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {platforms.map(platform => {
                    const color = getPlatformColor(platform.platform);
                    const pct = totalViews > 0 ? ((platform.views / totalViews) * 100).toFixed(1) : '0';
                    return (
                      <div key={platform.platform} className="rounded-xl p-4 bg-gray-900 border border-gray-700 shadow-lg" style={{ borderBottom: `3px solid ${color}` }}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <PlatformIcon platform={platform.platform} size="md" />
                            <span className="text-sm text-white/70">{fmtPlatform(platform.platform)}</span>
                          </div>
                          <span className="text-xs text-white/40">{platform.contentCount} pieces</span>
                        </div>
                        <div className="text-xl font-bold text-white mb-2">{formatFullNumber(platform.views)}</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                          </div>
                          <span className="text-xs font-medium" style={{ color }}>{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Insights - Donut Charts */}
                <div>
                  <h4 className="text-sm font-semibold text-white/70 mb-3">Insights</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <ReportDonutChart
                      data={coverageData}
                      centerValue={coverageTotal > 0 ? `${((covered / coverageTotal) * 100).toFixed(0)}%` : '0%'}
                      centerLabel="Coverage"
                      title="Creator Coverage"
                    />
                    {platformSplitData.length > 0 && (
                      <ReportDonutChart
                        data={platformSplitData}
                        centerValue={formatFullNumber(totalViews)}
                        centerLabel="Total Views"
                        title="Platform Split"
                      />
                    )}
                    {regionData.length > 0 && (
                      <ReportDonutChart
                        data={regionData}
                        centerValue={regionCreatorCount.toString()}
                        centerLabel="Creators"
                        title="Region Breakdown"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* After Executive Summary Image Block */}
        <div className="mb-6">
          {getBlocksForPosition('after-executive').length > 0 ? (
            <div className="rounded-xl overflow-hidden bg-gray-900 border border-gray-700 shadow-lg">
              {getBlocksForPosition('after-executive').map((block) => (
                <div key={block.id} className="relative group">
                  <img
                    src={block.src}
                    alt={block.caption || 'Section image'}
                    className="w-full h-40 object-cover"
                  />
                  {block.caption && (
                    <p className="text-center text-sm text-white/50 italic py-2 bg-white/5">{block.caption}</p>
                  )}
                  <button
                    onClick={() => handleDeleteImageBlock(block.id)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-50 rounded text-xs text-white">
                    After Executive Summary
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <button
              onClick={() => handleAddImageBlock('after-executive')}
              className="w-full p-4 border-2 border-dashed border-white/15 rounded-xl hover:border-sc-pink hover:bg-sc-pink/10 transition-colors flex items-center justify-center space-x-3"
            >
              <ImageIcon className="w-5 h-5 text-white/40" />
              <span className="text-sm font-medium text-white/50">Add Image After Executive Summary</span>
            </button>
          )}
        </div>

        {/* Video Content Section */}
        <div className="rounded-2xl mb-6 overflow-hidden relative bg-gray-900 border border-gray-700 shadow-lg">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#ef4444' }} />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#f59e0b' }} />
          <button
            onClick={() => toggleSection('video')}
            className="w-full px-6 py-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors relative z-10"
          >
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Video className="w-5 h-5 mr-2 text-red-500" />
              Video Content
              {adjustedMetrics.videoMetrics.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                  {adjustedMetrics.videoMetrics.count} videos
                </span>
              )}
            </h3>
            {expandedSections.video ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>
          
          {expandedSections.video && (
            <div className="p-6 space-y-6 relative z-10">
              {/* Video Metrics */}
              {reportData?.videoSection && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { key: 'videoViews', label: 'Total Views', value: formatFullNumber(adjustedMetrics.videoMetrics.totalViews), accent: '#ef4444' },
                      { key: 'videoLikes', label: 'Total Likes', value: formatFullNumber(adjustedMetrics.videoMetrics.totalLikes), accent: '#f97316' },
                      { key: 'videoComments', label: 'Comments', value: formatFullNumber(adjustedMetrics.videoMetrics.totalComments), accent: '#22c55e' },
                      { key: 'videoShares', label: 'Shares', value: formatFullNumber(adjustedMetrics.videoMetrics.totalShares), accent: '#3b82f6' },
                      { key: 'videoEngRate', label: 'Eng. Rate', value: formatPercent(adjustedMetrics.videoMetrics.engagementRate), accent: '#a855f7', tooltip: 'engagement' },
                    ].map(metric => {
                      const visible = metricVisibility[metric.key];
                      return (
                        <div
                          key={metric.key}
                          className={`relative rounded-xl p-4 cursor-pointer transition-all overflow-hidden border ${
                            visible ? 'bg-gray-900 border-gray-700 shadow-lg' : 'bg-white/5 border-white/10 opacity-50'
                          }`}
                          onClick={() => toggleMetricVisibility(metric.key)}
                        >
                          <div className={`absolute top-2 right-2 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center z-10 ${visible ? 'bg-sc-pink border-sc-pink' : 'bg-white/10 border-white/30'}`}>
                            {visible && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                          </div>
                          {visible && (
                            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: metric.accent }} />
                          )}
                          <div className="relative z-10">
                            <div className="flex items-center gap-1.5 mb-1">
                              <p className={`text-sm font-medium ${visible ? 'text-white/60' : 'text-gray-500'}`}>{metric.label}</p>
                              {metric.tooltip && <YouTubeEstimateTooltip metric={metric.tooltip} />}
                            </div>
                            <p className={`text-xl font-bold ${visible ? 'text-white' : 'text-gray-600'}`}>{metric.value}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-white/40 mt-1">Click metrics to toggle visibility in PDF</p>

                  {/* Video Timeline Chart - Platform Lines */}
                  {safeReportData.videoSection.timeline?.length > 0 && (() => {
                    // Check which platforms have data
                    const hasYouTube = safeReportData.videoSection.timeline.some(d => d.youtube?.views > 0);
                    const hasTikTok = safeReportData.videoSection.timeline.some(d => d.tiktok?.views > 0);
                    const hasInstagram = safeReportData.videoSection.timeline.some(d => d.instagram?.views > 0);
                    const hasAggregateViews = safeReportData.videoSection.timeline.some(d => d.views > 0);
                    const hasPlatformData = hasYouTube || hasTikTok || hasInstagram;

                    // Prepare data with platform-specific values
                    const chartData = safeReportData.videoSection.timeline.map(d => ({
                      ...d,
                      youtubeViews: d.youtube?.views || 0,
                      tiktokViews: d.tiktok?.views || 0,
                      instagramViews: d.instagram?.views || 0
                    }));

                    return (
                      <div style={{ width: '100%', height: '300px' }}>
                        <p className="text-sm font-medium text-white/70 mb-2">Video Views Over Time {hasPlatformData ? '(by Platform)' : ''}</p>
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_DARK.grid} />
                            <XAxis dataKey="period" tick={CHART_DARK.tick} />
                            <YAxis tick={CHART_DARK.tick} tickFormatter={formatNumber} />
                            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={CHART_DARK.tooltip} itemStyle={{ color: '#fff' }} labelStyle={{ color: 'rgba(255,255,255,0.6)' }} />
                            <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                            {hasYouTube && <Line type="monotone" dataKey="youtubeViews" name="YouTube" stroke="#FF0000" strokeWidth={2} dot={{ fill: '#FF0000', r: 3 }} connectNulls />}
                            {hasTikTok && <Line type="monotone" dataKey="tiktokViews" name="TikTok" stroke="#69C9D0" strokeWidth={2} dot={{ fill: '#69C9D0', r: 3 }} connectNulls />}
                            {hasInstagram && <Line type="monotone" dataKey="instagramViews" name="Instagram" stroke="#E1306C" strokeWidth={2} dot={{ fill: '#E1306C', r: 3 }} connectNulls />}
                            {!hasPlatformData && hasAggregateViews && <Line type="monotone" dataKey="views" name="Views" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 3 }} connectNulls />}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })()}

                  {/* Video Content Analysis */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Video Content Analysis
                    </label>
                    <textarea
                      value={formData.videoAnalysis}
                      onChange={(e) => handleInputChange('videoAnalysis', e.target.value)}
                      placeholder="Analyze the video content performance, highlight top performers, and provide insights..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink resize-none text-white placeholder-white/30"
                      style={DARK_INPUT}
                    />
                  </div>

                  {/* Video Grid View by Platform */}
                  {safeReportData.videoSection.content?.length > 0 && (
                    <VideoBySectionView
                      content={safeReportData.videoSection.content}
                      reportId={reportId}
                      excludedContentIds={excludedContentIds}
                      onContentExcluded={(data) => {
                        setExcludedContentIds(data.excludedContentIds || []);
                      }}
                      sortBy={videoSortBy}
                      onSortChange={setVideoSortBy}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* After Video Content Image Block */}
        <div className="mb-6">
          {getBlocksForPosition('after-video').length > 0 ? (
            <div className="rounded-xl overflow-hidden bg-gray-900 border border-gray-700 shadow-lg">
              {getBlocksForPosition('after-video').map((block) => (
                <div key={block.id} className="relative group">
                  <img
                    src={block.src}
                    alt={block.caption || 'Section image'}
                    className="w-full h-40 object-cover"
                  />
                  {block.caption && (
                    <p className="text-center text-sm text-white/50 italic py-2 bg-white/5">{block.caption}</p>
                  )}
                  <button
                    onClick={() => handleDeleteImageBlock(block.id)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-50 rounded text-xs text-white">
                    After Video Content
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <button
              onClick={() => handleAddImageBlock('after-video')}
              className="w-full p-4 border-2 border-dashed border-white/15 rounded-xl hover:border-sc-pink hover:bg-sc-pink/10 transition-colors flex items-center justify-center space-x-3"
            >
              <ImageIcon className="w-5 h-5 text-white/40" />
              <span className="text-sm font-medium text-white/50">Add Image After Video Content</span>
            </button>
          )}
        </div>

        {/* Streaming Section */}
        <div className="rounded-2xl mb-6 overflow-hidden relative bg-gray-900 border border-gray-700 shadow-lg">
          <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#8b5cf6' }} />
          <div className="absolute -bottom-20 -right-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#06b6d4' }} />
          <button
            onClick={() => toggleSection('streaming')}
            className="w-full px-6 py-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors relative z-10"
          >
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Radio className="w-5 h-5 mr-2 text-purple-500" />
              Streaming Content
              {adjustedMetrics.streamMetrics.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                  {adjustedMetrics.streamMetrics.count} streams
                </span>
              )}
            </h3>
            {expandedSections.streaming ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>
          
          {expandedSections.streaming && (
            <div className="p-6 space-y-6 relative z-10">
              {/* Streaming Metrics */}
              {reportData?.streamingSection && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'avgCcv', label: 'Combined CCV', value: formatFullNumber(adjustedMetrics.streamMetrics.averageCcv), accent: '#a855f7' },
                      { key: 'peakCcv', label: 'Combined Peak CCV', value: formatFullNumber(adjustedMetrics.streamMetrics.peakCcvMax), accent: '#8b5cf6' },
                      { key: 'watchHours', label: 'Watch Hours', value: formatWatchHours(adjustedMetrics.streamMetrics.totalWatchHours), accent: '#06b6d4', tooltip: 'watchHours' },
                      { key: 'vodViews', label: 'VOD Views', value: formatFullNumber(adjustedMetrics.streamMetrics.totalVodViews), accent: '#f97316' },
                    ].map(metric => {
                      const visible = metricVisibility[metric.key];
                      return (
                        <div
                          key={metric.key}
                          className={`relative rounded-xl p-4 cursor-pointer transition-all overflow-hidden border ${
                            visible ? 'bg-gray-900 border-gray-700 shadow-lg' : 'bg-white/5 border-white/10 opacity-50'
                          }`}
                          onClick={() => toggleMetricVisibility(metric.key)}
                        >
                          <div className={`absolute top-2 right-2 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center z-10 ${visible ? 'bg-sc-pink border-sc-pink' : 'bg-white/10 border-white/30'}`}>
                            {visible && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                          </div>
                          {visible && (
                            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: metric.accent }} />
                          )}
                          <div className="relative z-10">
                            <div className="flex items-center gap-1.5 mb-1">
                              <p className={`text-sm font-medium ${visible ? 'text-white/60' : 'text-gray-500'}`}>{metric.label}</p>
                              {metric.tooltip && <YouTubeEstimateTooltip metric={metric.tooltip} />}
                            </div>
                            <p className={`text-xl font-bold ${visible ? 'text-white' : 'text-gray-600'}`}>{metric.value}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Streaming Timeline Chart - Platform Lines */}
                  {safeReportData.streamingSection.timeline?.length > 0 && (() => {
                    // Check which platforms have data
                    const hasTwitch = safeReportData.streamingSection.timeline.some(d => d.twitch?.avgCcv > 0);
                    const hasYouTube = safeReportData.streamingSection.timeline.some(d => d.youtube?.avgCcv > 0);
                    const hasAggregateAvgCcv = safeReportData.streamingSection.timeline.some(d => d.avgCcv > 0);
                    const hasPlatformData = hasTwitch || hasYouTube;

                    // Prepare data with platform-specific values
                    const chartData = safeReportData.streamingSection.timeline.map(d => ({
                      ...d,
                      twitchAvgCcv: d.twitch?.avgCcv || 0,
                      youtubeAvgCcv: d.youtube?.avgCcv || 0
                    }));

                    return (
                      <div style={{ width: '100%', height: '300px' }}>
                        <p className="text-sm font-medium text-white/70 mb-2">Streaming Performance Over Time {hasPlatformData ? '(by Platform)' : ''}</p>
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_DARK.grid} />
                            <XAxis dataKey="period" tick={CHART_DARK.tick} />
                            <YAxis tick={CHART_DARK.tick} tickFormatter={formatNumber} />
                            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={CHART_DARK.tooltip} itemStyle={{ color: '#fff' }} />
                            <Legend />
                            {hasTwitch && <Line type="monotone" dataKey="twitchAvgCcv" name="Twitch Combined CCV" stroke="#9146FF" strokeWidth={2} dot={{ fill: '#9146FF', r: 3 }} connectNulls />}
                            {hasYouTube && <Line type="monotone" dataKey="youtubeAvgCcv" name="YouTube Combined CCV" stroke="#FF0000" strokeWidth={2} dot={{ fill: '#FF0000', r: 3 }} connectNulls />}
                            {!hasPlatformData && hasAggregateAvgCcv && <Line type="monotone" dataKey="avgCcv" name="Combined CCV" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 3 }} connectNulls />}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })()}
                  
                  {/* Streaming Content Analysis */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Streaming Content Analysis
                    </label>
                    <textarea
                      value={formData.streamingAnalysis}
                      onChange={(e) => handleInputChange('streamingAnalysis', e.target.value)}
                      placeholder="Analyze the streaming performance, viewer engagement, and key insights..."
                      rows={5}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink resize-none text-white placeholder-white/30"
                      style={DARK_INPUT}
                    />
                  </div>
                  
                  {/* Streaming Grid View by Platform */}
                  {safeReportData.streamingSection.content?.length > 0 && (
                    <StreamingBySectionView
                      content={safeReportData.streamingSection.content}
                      reportId={reportId}
                      excludedContentIds={excludedContentIds}
                      onContentExcluded={(data) => {
                        setExcludedContentIds(data.excludedContentIds || []);
                      }}
                      sortBy={streamingSortBy}
                      onSortChange={setStreamingSortBy}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* After Streaming Content Image Block */}
        <div className="mb-6">
          {getBlocksForPosition('after-streaming').length > 0 ? (
            <div className="rounded-xl overflow-hidden bg-gray-900 border border-gray-700 shadow-lg">
              {getBlocksForPosition('after-streaming').map((block) => (
                <div key={block.id} className="relative group">
                  <img
                    src={block.src}
                    alt={block.caption || 'Section image'}
                    className="w-full h-40 object-cover"
                  />
                  {block.caption && (
                    <p className="text-center text-sm text-white/50 italic py-2 bg-white/5">{block.caption}</p>
                  )}
                  <button
                    onClick={() => handleDeleteImageBlock(block.id)}
                    className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-50 rounded text-xs text-white">
                    After Streaming Content
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <button
              onClick={() => handleAddImageBlock('after-streaming')}
              className="w-full p-4 border-2 border-dashed border-white/15 rounded-xl hover:border-sc-pink hover:bg-sc-pink/10 transition-colors flex items-center justify-center space-x-3"
            >
              <ImageIcon className="w-5 h-5 text-white/40" />
              <span className="text-sm font-medium text-white/50">Add Image After Streaming Content</span>
            </button>
          )}
        </div>

        {/* Social Media Content Section (Instagram + Twitter posts) */}
        <div className="rounded-2xl mb-6 overflow-hidden relative bg-gray-900 border border-gray-700 shadow-lg">
          <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: '#E1306C' }} />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: '#1DA1F2' }} />
          <button
            onClick={() => toggleSection('socialMedia')}
            className="w-full px-6 py-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors relative z-10"
          >
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Share2 className="w-5 h-5 mr-2 text-pink-500" />
              Social Media Content
              {adjustedMetrics.socialMediaMetrics.count > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs rounded-full">
                  {adjustedMetrics.socialMediaMetrics.count} posts
                </span>
              )}
            </h3>
            {expandedSections.socialMedia ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>

          {expandedSections.socialMedia && (
            <div className="p-6 space-y-6 relative z-10">
              {reportData?.socialMediaSection && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {[
                      { key: 'socialMediaViews', label: 'Total Views', value: formatFullNumber(adjustedMetrics.socialMediaMetrics.totalViews), accent: '#E1306C' },
                      { key: 'socialMediaLikes', label: 'Total Likes', value: formatFullNumber(adjustedMetrics.socialMediaMetrics.totalLikes), accent: '#f97316' },
                      { key: 'socialMediaComments', label: 'Comments', value: formatFullNumber(adjustedMetrics.socialMediaMetrics.totalComments), accent: '#22c55e' },
                      { key: 'socialMediaShares', label: 'Shares', value: formatFullNumber(adjustedMetrics.socialMediaMetrics.totalShares), accent: '#1DA1F2' },
                      { key: 'socialMediaEngRate', label: 'Eng. Rate', value: formatPercent(adjustedMetrics.socialMediaMetrics.engagementRate), accent: '#a855f7', tooltip: 'engagement' },
                    ].map(metric => {
                      const visible = metricVisibility[metric.key];
                      return (
                        <div
                          key={metric.key}
                          className={`relative rounded-xl p-4 cursor-pointer transition-all overflow-hidden border ${
                            visible ? 'bg-gray-900 border-gray-700 shadow-lg' : 'bg-white/5 border-white/10 opacity-50'
                          }`}
                          onClick={() => toggleMetricVisibility(metric.key)}
                        >
                          <div className={`absolute top-2 right-2 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center z-10 ${visible ? 'bg-sc-pink border-sc-pink' : 'bg-white/10 border-white/30'}`}>
                            {visible && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                          </div>
                          {visible && (
                            <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: metric.accent }} />
                          )}
                          <div className="relative z-10">
                            <div className="flex items-center gap-1.5 mb-1">
                              <p className={`text-sm font-medium ${visible ? 'text-white/60' : 'text-gray-500'}`}>{metric.label}</p>
                              {metric.tooltip && <YouTubeEstimateTooltip metric={metric.tooltip} />}
                            </div>
                            <p className={`text-xl font-bold ${visible ? 'text-white' : 'text-gray-600'}`}>{metric.value}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-white/40 mt-1">Click metrics to toggle visibility in PDF</p>

                  {/* Social Media Timeline Chart - Platform Lines */}
                  {safeReportData.socialMediaSection.timeline?.length > 0 && (() => {
                    const hasInstagram = safeReportData.socialMediaSection.timeline.some(d => d.instagram?.views > 0);
                    const hasTwitter = safeReportData.socialMediaSection.timeline.some(d => d.twitter?.views > 0);
                    const hasAggregateViews = safeReportData.socialMediaSection.timeline.some(d => d.views > 0);
                    const hasPlatformData = hasInstagram || hasTwitter;

                    const chartData = safeReportData.socialMediaSection.timeline.map(d => ({
                      ...d,
                      instagramViews: d.instagram?.views || 0,
                      twitterViews: d.twitter?.views || 0
                    }));

                    return (
                      <div style={{ width: '100%', height: '300px' }}>
                        <p className="text-sm font-medium text-white/70 mb-2">Social Media Views Over Time {hasPlatformData ? '(by Platform)' : ''}</p>
                        <ResponsiveContainer width="100%" height={250}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke={CHART_DARK.grid} />
                            <XAxis dataKey="period" tick={CHART_DARK.tick} />
                            <YAxis tick={CHART_DARK.tick} tickFormatter={formatNumber} />
                            <Tooltip formatter={(value) => formatNumber(value)} contentStyle={CHART_DARK.tooltip} itemStyle={{ color: '#fff' }} labelStyle={{ color: 'rgba(255,255,255,0.6)' }} />
                            <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                            {hasInstagram && <Line type="monotone" dataKey="instagramViews" name="Instagram" stroke="#E1306C" strokeWidth={2} dot={{ fill: '#E1306C', r: 3 }} connectNulls />}
                            {hasTwitter && <Line type="monotone" dataKey="twitterViews" name="Twitter" stroke="#1DA1F2" strokeWidth={2} dot={{ fill: '#1DA1F2', r: 3 }} connectNulls />}
                            {!hasPlatformData && hasAggregateViews && <Line type="monotone" dataKey="views" name="Views" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 3 }} connectNulls />}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    );
                  })()}

                  {/* Social Media Content Analysis */}
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">
                      Social Media Content Analysis
                    </label>
                    <textarea
                      value={formData.socialMediaAnalysis}
                      onChange={(e) => handleInputChange('socialMediaAnalysis', e.target.value)}
                      placeholder="Analyze social media performance, highlight standout posts, note engagement patterns..."
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg focus:ring-2 focus:ring-sc-pink focus:border-sc-pink resize-none text-white placeholder-white/30"
                      style={DARK_INPUT}
                    />
                  </div>

                  {/* Social Media Content Grid */}
                  {safeReportData.socialMediaSection.content?.length > 0 && (
                    <SocialMediaBySectionView
                      content={safeReportData.socialMediaSection.content}
                      reportId={reportId}
                      excludedContentIds={excludedContentIds}
                      onContentExcluded={(data) => {
                        setExcludedContentIds(data.excludedContentIds || []);
                      }}
                      sortBy={socialMediaSortBy}
                      onSortChange={setSocialMediaSortBy}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Creator Performance - hidden, keeping for potential future use */}
        <div style={{ display: 'none' }}>
          <button>
            <h3 className="text-lg font-semibold text-white flex items-center">
              <Users className="w-5 h-5 mr-2 text-green-500" />
              Creator Performance
              {reportData?.creators && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  {safeReportData.creators.length} creators
                </span>
              )}
            </h3>
            {expandedSections.creators ? <ChevronUp className="w-5 h-5 text-white/60" /> : <ChevronDown className="w-5 h-5 text-white/60" />}
          </button>
          
          {expandedSections.creators && reportData?.creators && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Creator</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Content</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Views</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        <div className="flex items-center justify-end gap-1">Engagement <YouTubeEstimateTooltip metric="engagement" /></div>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        <div className="flex items-center justify-end gap-1">Eng. Rate <YouTubeEstimateTooltip metric="engagement" /></div>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        <div className="flex items-center justify-end gap-1">Combined CCV <YouTubeEstimateTooltip metric="watchHours" /></div>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        <div className="flex items-center justify-end gap-1">Watch Hrs <YouTubeEstimateTooltip metric="watchHours" /></div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {safeReportData.creators.map(item => (
                      <tr key={item.creator.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            {item.creator.profileImageUrl ? (
                              <img 
                                src={item.creator.profileImageUrl}
                                alt={item.creator.name}
                                className="w-8 h-8 rounded-full mr-3"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-200 mr-3" />
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{item.creator.name}</p>
                              <p className="text-xs text-gray-500">@{item.creator.handle}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2 py-1 rounded text-xs font-medium"
                            style={{ 
                              backgroundColor: getPlatformColor(item.creator.platform) + '20',
                              color: getPlatformColor(item.creator.platform)
                            }}
                          >
                            {item.creator.platform}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">{item.metrics.contentCount}</td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">{formatNumber(item.metrics.views)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatNumber(item.metrics.totalEngagement)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{formatPercent(item.metrics.engagementRate)}</td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {item.metrics.avgCcv > 0 ? formatNumber(item.metrics.avgCcv) : '-'}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {item.metrics.watchHours > 0 ? formatWatchHours(item.metrics.watchHours) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ROI Section */}
        {reportData?.roi && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
              ROI & Spend Analysis
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm text-emerald-600">Total Spend</p>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(safeReportData.roi.totalSpend)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm text-emerald-600">Cost Per View</p>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(safeReportData.roi.costPerView)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm text-emerald-600">CPM</p>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(safeReportData.roi.costPer1000Views)}</p>
              </div>
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-sm text-emerald-600">Cost Per Engagement</p>
                <p className="text-2xl font-bold text-emerald-900">{formatCurrency(safeReportData.roi.costPerEngagement)}</p>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Image Block Picker Modal */}
      <ImageBlockPicker
        isOpen={showImagePicker}
        onClose={() => {
          setShowImagePicker(false);
          setImageInsertPosition(null);
        }}
        onSelect={handleImageSelected}
      />

      {/* Full Preview Modal */}
      {showFullPreview && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowFullPreview(false)}
          />

          {/* Modal Container */}
          <div className="absolute inset-4 md:inset-8 lg:inset-12 flex flex-col bg-gray-950 rounded-xl shadow-2xl overflow-hidden border border-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-900">
              <div className="flex items-center gap-3">
                <Maximize2 className="w-5 h-5 text-sc-pink" />
                <h2 className="text-lg font-semibold text-white">Full Report Preview</h2>
                <span className="text-sm text-white/50">
                  {formData.title || campaignFullData?.campaign?.name || 'Campaign Report'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleExportPDF}
                  className="inline-flex items-center px-4 py-2 bg-sc-pink text-white rounded-lg text-sm font-medium hover:bg-sc-pink/90 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </button>
                <button
                  onClick={() => setShowFullPreview(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/50" />
                </button>
              </div>
            </div>

            {/* Preview Content - Scrollable */}
            <div
              className="flex-1 overflow-auto p-8 relative"
              style={(() => {
                const s = { backgroundColor: '#0a0a1a' };
                let bgUrl = fullReportData?.campaign?.gameImage || reportData?.campaign?.gameImage;
                if (bgUrl) {
                  if (bgUrl.includes('images.igdb.com') && bgUrl.includes('/t_')) {
                    bgUrl = bgUrl.replace(/\/t_[^/]+\//, '/t_720p/');
                  }
                  s.backgroundImage = `url(${bgUrl})`;
                  s.backgroundSize = 'cover';
                  s.backgroundPosition = 'center';
                  s.backgroundAttachment = 'fixed';
                  s.backgroundRepeat = 'no-repeat';
                }
                return s;
              })()}
            >
              {/* Dark overlay for background image */}
              <div className="fixed inset-0 pointer-events-none" style={{
                background: 'linear-gradient(180deg, rgba(10,10,26,0.65) 0%, rgba(10,10,26,0.82) 35%, rgba(10,10,26,0.92) 65%, rgba(10,10,26,0.97) 100%)',
              }} />
              <div className="max-w-4xl mx-auto space-y-8 relative z-10">
                {/* Page 1: Executive Summary */}
                <div
                  className="bg-gray-900 border border-gray-700 rounded-xl shadow-lg p-8 relative"
                  style={{ fontFamily: gameBranding?.fontFamily || 'Helvetica' }}
                >
                  <div className="absolute top-4 right-4 px-2 py-1 bg-white/10 text-white/50 text-xs rounded">Page 1</div>

                  {/* Header with Logo */}
                  {gameBranding?.logoUrl && (
                    <img
                      src={gameBranding.logoUrl}
                      alt="Logo"
                      className="h-12 mb-4 object-contain"
                    />
                  )}

                  {/* Header Image Block */}
                  {contentBlocks.filter(b => b.position === 'header').map((block, idx) => (
                    <img key={idx} src={block.src} alt="" className="w-full h-32 object-cover rounded-lg mb-6" />
                  ))}

                  <h1 className="text-3xl font-bold mb-2 text-white">
                    {formData.title || campaignFullData?.campaign?.name || 'Campaign Report'}
                  </h1>
                  <p className="text-white/60 mb-1">
                    {campaignFullData?.campaign?.game && `${typeof campaignFullData.campaign.game === 'object' ? (campaignFullData.campaign.game?.title || campaignFullData.campaign.game?.name) : campaignFullData.campaign.game} • `}
                    {campaignFullData?.campaign?.clientName || 'Client Report'}
                  </p>
                  {(formData.reportPeriodStart || formData.reportPeriodEnd) && (
                    <p
                      className="inline-block px-3 py-1 rounded-full text-sm mb-6"
                      style={{
                        backgroundColor: `${gameBranding?.primaryColor || '#fe2388'}25`,
                        color: gameBranding?.primaryColor || '#fe2388'
                      }}
                    >
                      Report Period: {formatDate(formData.reportPeriodStart)} - {formatDate(formData.reportPeriodEnd)}
                    </p>
                  )}

                  {/* Executive Summary Section */}
                  <h2
                    className="text-xl font-semibold pb-2 mb-4 border-b-2 text-white"
                    style={{ borderColor: gameBranding?.primaryColor || '#fe2388' }}
                  >
                    Executive Summary
                  </h2>

                  {/* Hero KPI Cards — portal style */}
                  {(() => {
                    const previewVideoContent = (safeReportData.videoSection?.content || []).filter(c => !excludedContentIds.includes(c.id));
                    const previewStreamContent = (safeReportData.streamingSection?.content || []).filter(c => !excludedContentIds.includes(c.id));
                    const previewAllContent = [...previewVideoContent, ...previewStreamContent];

                    const heroKpis = [
                      { key: 'totalEstimatedImpressions', label: 'Total Combined Views', value: formatFullNumber(adjustedMetrics.summary.views), accent: '#e91e8a' },
                      { key: 'totalViews', label: 'Impressions', value: formatFullNumber(adjustedMetrics.summary.totalEstimatedImpressions), accent: '#f59e0b' },
                      { key: 'totalLikes', label: 'Engagement', value: formatFullNumber(adjustedMetrics.summary.totalLikes), accent: '#22c55e' },
                      { key: 'watchHours', label: 'Watch Hours', value: formatWatchHours(adjustedMetrics.summary.watchHours), accent: '#3b82f6' },
                    ].filter(kpi => metricVisibility[kpi.key] !== false);

                    const platformViews = {};
                    previewAllContent.forEach(c => {
                      const p = c.platform?.toLowerCase();
                      if (p) platformViews[p] = (platformViews[p] || 0) + (c.views || c.vodViews || 0);
                    });

                    const previewPlatforms = safeReportData.platforms || [];
                    const fmtPP = (p) => ({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', twitter: 'Twitter', instagram: 'Instagram' }[p?.toLowerCase()] || p);

                    const secondaryStats = [
                      { label: 'Content', value: previewAllContent.length },
                      metricVisibility.avgCcv !== false && { label: 'Combined CCV', value: adjustedMetrics.summary.avgCcv },
                      metricVisibility.engagementRate !== false && { label: 'Combined Peak CCV', value: adjustedMetrics.streamMetrics.peakCcvMax },
                      adjustedMetrics.streamMetrics.totalVodViews > 0 && { label: 'VOD Views', value: adjustedMetrics.streamMetrics.totalVodViews },
                      ...previewPlatforms.filter(p => (p.views || 0) > 0 && !['twitch', 'kick'].includes(p.platform?.toLowerCase())).map(p => ({ label: `${fmtPP(p.platform)} Views`, value: p.views })),
                    ].filter(Boolean);

                    return (
                      <>
                        <div className="grid grid-cols-4 gap-2 mb-2">
                          {heroKpis.map((kpi, i) => (
                            <div key={i} className="relative rounded-lg p-3 bg-gray-900 overflow-hidden">
                              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full opacity-20 blur-2xl pointer-events-none" style={{ background: kpi.accent }} />
                              <div className="relative z-10">
                                <div className="flex items-center gap-1 mb-0.5">
                                  <span className="text-[9px] font-medium text-white/60">{kpi.label}</span>
                                </div>
                                <div className="text-lg font-bold text-white">{kpi.value}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {secondaryStats.map((stat, i) => (
                            <div key={i} className="rounded-md bg-gray-800 border border-gray-700 px-3 py-2 text-center flex-1 min-w-[70px]">
                              <p className="text-[9px] text-white/50 mb-0.5">{stat.label}</p>
                              <p className="text-sm font-bold text-white">{typeof stat.value === 'number' ? formatFullNumber(stat.value) : stat.value}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}

                  {/* Analysis Sections */}
                  {formData.overallAnalysis && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold mb-1" style={{ color: gameBranding?.primaryColor || '#fe2388' }}>
                        Overall Campaign Analysis
                      </h3>
                      <p className="text-white/70 text-sm whitespace-pre-wrap">{formData.overallAnalysis}</p>
                    </div>
                  )}

                  {/* After Executive Image Block */}
                  {contentBlocks.filter(b => b.position === 'after-executive').map((block, idx) => (
                    <img key={idx} src={block.src} alt="" className="w-full h-44 object-cover rounded-lg my-4" />
                  ))}

                  {existingReport?.updatedAt && (
                    <p className="text-xs text-white/30 mt-6 pt-4 border-t border-white/10">
                      YouTube &amp; Instagram data last refreshed: {new Date(existingReport.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>

                {/* Page 2: Video Content - Only show if there's content after filtering */}
                {(reportData?.videoSection?.content || []).filter(v => !excludedContentIds.includes(v.id)).length > 0 && (
                  <div
                    className="bg-gray-900 border border-gray-700 rounded-xl shadow-lg p-8 relative"
                    style={{ fontFamily: gameBranding?.fontFamily || 'Helvetica' }}
                  >
                    <div className="absolute top-4 right-4 px-2 py-1 bg-white/10 text-white/50 text-xs rounded">Page 2</div>

                    <h2
                      className="text-xl font-semibold pb-2 mb-4 border-b-2 text-white"
                      style={{ borderColor: gameBranding?.secondaryColor || '#ef4444' }}
                    >
                      Video Content Performance
                    </h2>

                    {/* Video Metrics - Uses adjustedMetrics and respects visibility */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[
                        { key: 'videoViews', label: 'Total Views', value: formatFullNumber(adjustedMetrics.videoMetrics.totalViews), accent: '#ef4444' },
                        { key: 'videoLikes', label: 'Total Likes', value: formatFullNumber(adjustedMetrics.videoMetrics.totalLikes), accent: '#f97316' },
                        { key: 'videoComments', label: 'Comments', value: formatFullNumber(adjustedMetrics.videoMetrics.totalComments), accent: '#22c55e' },
                        { key: 'videoShares', label: 'Shares', value: formatFullNumber(adjustedMetrics.videoMetrics.totalShares), accent: '#3b82f6' },
                        { key: 'videoEngRate', label: 'Eng. Rate', value: formatPercent(adjustedMetrics.videoMetrics.engagementRate), accent: '#a855f7' },
                        { key: 'videoCount', label: 'Videos', value: adjustedMetrics.videoMetrics.count, accent: '#06b6d4' },
                      ].filter(metric => metricVisibility[metric.key] !== false).map((metric, idx) => (
                        <div
                          key={idx}
                          className="p-3 rounded-lg relative overflow-hidden bg-gray-800 border border-gray-700"
                        >
                          <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full opacity-20 blur-xl pointer-events-none" style={{ background: metric.accent }} />
                          <p className="text-xs text-white/50 mb-1 relative z-10">{metric.label}</p>
                          <p className="text-lg font-bold text-white relative z-10">
                            {metric.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Video Timeline Chart - Platform Lines */}
                    {reportData.videoSection.timeline?.length > 0 && (() => {
                      const hasYouTube = reportData.videoSection.timeline.some(d => d.youtube?.views > 0);
                      const hasTikTok = reportData.videoSection.timeline.some(d => d.tiktok?.views > 0);
                      const hasInstagram = reportData.videoSection.timeline.some(d => d.instagram?.views > 0);
                      const hasAggregateViews = reportData.videoSection.timeline.some(d => d.views > 0);
                      const hasPlatformData = hasYouTube || hasTikTok || hasInstagram;
                      const chartData = reportData.videoSection.timeline.map(d => ({
                        ...d,
                        youtubeViews: d.youtube?.views || 0,
                        tiktokViews: d.tiktok?.views || 0,
                        instagramViews: d.instagram?.views || 0
                      }));

                      return (
                        <div className="mb-6">
                          <p className="text-sm font-medium text-white/70 mb-2">Video Views Over Time {hasPlatformData ? '(by Platform)' : ''}</p>
                          <div style={{ width: '100%', height: '200px' }}>
                            <ResponsiveContainer width="100%" height={180}>
                              <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="period" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} />
                                <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={formatNumber} />
                                <Tooltip formatter={(value) => formatNumber(value)} contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: 'rgba(255,255,255,0.6)' }} />
                                <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                                {hasYouTube && <Line type="monotone" dataKey="youtubeViews" name="YouTube" stroke="#FF0000" strokeWidth={2} dot={{ fill: '#FF0000', r: 2 }} connectNulls />}
                                {hasTikTok && <Line type="monotone" dataKey="tiktokViews" name="TikTok" stroke="#69C9D0" strokeWidth={2} dot={{ fill: '#69C9D0', r: 2 }} connectNulls />}
                                {hasInstagram && <Line type="monotone" dataKey="instagramViews" name="Instagram" stroke="#E1306C" strokeWidth={2} dot={{ fill: '#E1306C', r: 2 }} connectNulls />}
                                {!hasPlatformData && hasAggregateViews && <Line type="monotone" dataKey="views" name="Views" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 2 }} connectNulls />}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()}

                    {formData.videoAnalysis && (
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold mb-1" style={{ color: gameBranding?.secondaryColor || '#ef4444' }}>
                          Video Content Analysis
                        </h3>
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{formData.videoAnalysis}</p>
                      </div>
                    )}

                    {/* Video Content by Platform - Card Grid Layout (matches editor) */}
                    {(() => {
                      // Sort function matching ContentGridView
                      const sortContent = (arr, sortKey) => {
                        return [...arr].sort((a, b) => {
                          switch (sortKey) {
                            case 'date-newest': return new Date(b.postDate) - new Date(a.postDate);
                            case 'date-oldest': return new Date(a.postDate) - new Date(b.postDate);
                            case 'views-high': return (b.views || 0) - (a.views || 0);
                            case 'views-low': return (a.views || 0) - (b.views || 0);
                            case 'likes-high': return (b.likes || 0) - (a.likes || 0);
                            case 'avgCcv-high': return (b.avgCcv || 0) - (a.avgCcv || 0);
                            case 'peakCcv-high': return (b.peakCcv || 0) - (a.peakCcv || 0);
                            case 'watchHours-high': return (b.watchHours || 0) - (a.watchHours || 0);
                            default: return 0;
                          }
                        });
                      };
                      const filteredVideos = (reportData.videoSection.content || []).filter(v => !excludedContentIds.includes(v.id));
                      const groupedByPlatform = {};
                      filteredVideos.forEach(video => {
                        const platform = video.platform?.toLowerCase() || 'other';
                        if (!groupedByPlatform[platform]) groupedByPlatform[platform] = [];
                        groupedByPlatform[platform].push(video);
                      });
                      // Apply sorting to each platform group
                      Object.keys(groupedByPlatform).forEach(p => {
                        groupedByPlatform[p] = sortContent(groupedByPlatform[p], videoSortBy);
                      });
                      // Merge Instagram into TikTok group
                      if (groupedByPlatform['instagram']?.length > 0) {
                        groupedByPlatform['tiktok'] = sortContent([...(groupedByPlatform['tiktok'] || []), ...groupedByPlatform['instagram']], videoSortBy);
                        delete groupedByPlatform['instagram'];
                      }
                      const hasInstagramVideos = filteredVideos.some(v => v.platform?.toLowerCase() === 'instagram');
                      const hasTiktokVideos = filteredVideos.some(v => v.platform?.toLowerCase() === 'tiktok');
                      const tiktokLabel = hasTiktokVideos && hasInstagramVideos ? 'TikTok & Instagram Videos' : hasInstagramVideos ? 'Instagram Videos' : 'TikTok Videos';
                      const platformOrder = ['tiktok', 'youtube'];
                      const platformLabels = { youtube: 'YouTube Videos', tiktok: tiktokLabel, twitch: 'Twitch VODs', other: 'Other Videos' };
                      const platformColors = { youtube: '#FF0000', tiktok: '#000000', twitch: '#9146FF', kick: '#00E70A', instagram: '#E4405F', other: '#6B7280' };
                      const platformsToShow = platformOrder.filter(p => groupedByPlatform[p]?.length > 0);

                      return (
                        <div className="space-y-6">
                          {platformsToShow.map(platform => (
                            <div key={platform}>
                              {/* Platform Header */}
                              <div className="flex items-center justify-between bg-gray-800 px-3 py-2 rounded-lg border border-gray-700 mb-3">
                                <h3 className="text-sm font-semibold text-white">
                                  {platformLabels[platform] || `${({ twitch: 'Twitch', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' }[platform] || platform)} Videos`}
                                </h3>
                                <span className="text-xs font-medium text-white/50 bg-white/10 px-2 py-1 rounded">
                                  {groupedByPlatform[platform].length} video{groupedByPlatform[platform].length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {/* Video Card Grid - Limited to 9 per platform */}
                              <div className="grid grid-cols-3 gap-3">
                                {groupedByPlatform[platform].slice(0, 9).map((video, idx) => {
                                  const videoPlatform = video.platform?.toLowerCase() || platform;
                                  return (
                                  <div key={idx} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                                    {/* Thumbnail */}
                                    <div className="relative bg-gray-900 aspect-video">
                                      {video.thumbnailUrl ? (
                                        <img src={resolveThumbnailUrl(video.thumbnailUrl)} alt={video.title} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                                          <span className="text-gray-500 text-xs">No Thumbnail</span>
                                        </div>
                                      )}
                                      {/* Platform Badge - show actual platform per video */}
                                      <span
                                        className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                                        style={{ backgroundColor: (platformColors[videoPlatform] || platformColors[platform] || '#6B7280') + 'dd' }}
                                      >
                                        {({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' }[videoPlatform] || platform)}
                                      </span>
                                      {/* Creator Avatar */}
                                      {video.creator?.profileImageUrl && (
                                        <img
                                          src={video.creator.profileImageUrl}
                                          alt=""
                                          className="absolute top-1 left-1 w-5 h-5 rounded-full border border-white object-cover"
                                        />
                                      )}
                                      {/* Paid badge */}
                                      {video.isPaid && (
                                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sc-pink text-white">💰 Paid</span>
                                      )}
                                    </div>
                                    {/* Content Details */}
                                    <div className="p-2 space-y-1">
                                      <p className="text-[10px] font-medium text-white line-clamp-2">{video.title || 'Untitled'}</p>
                                      <p className="text-[9px] text-white/50">{video.creator?.name}</p>
                                      {/* Metrics */}
                                      <div className="grid grid-cols-2 gap-1 pt-1 border-t border-white/10 text-[9px]">
                                        <div><span className="text-white/50">Views:</span> <span className="font-medium text-white">{formatFullNumber(video.views)}</span></div>
                                        <div><span className="text-white/50">Likes:</span> <span className="font-medium text-white">{formatFullNumber(video.likes)}</span></div>
                                        <div><span className="text-white/50">Comments:</span> <span className="font-medium text-white">{formatFullNumber(video.comments)}</span></div>
                                        <div><span className="text-white/50">Eng:</span> <span className="font-medium text-white">{formatPercent(video.engagementRate || (video.views > 0 ? ((video.likes + video.comments) / video.views) * 100 : 0))}</span></div>
                                      </div>
                                    </div>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* After Video Image Block */}
                    {contentBlocks.filter(b => b.position === 'after-video').map((block, idx) => (
                      <img key={idx} src={block.src} alt="" className="w-full h-44 object-cover rounded-lg my-4" />
                    ))}
                  </div>
                )}

                {/* Page 3: Streaming Content - Only show if there's content after filtering */}
                {(reportData?.streamingSection?.content || []).filter(s => !excludedContentIds.includes(s.id)).length > 0 && (
                  <div
                    className="bg-gray-900 border border-gray-700 rounded-xl shadow-lg p-8 relative"
                    style={{ fontFamily: gameBranding?.fontFamily || 'Helvetica' }}
                  >
                    <div className="absolute top-4 right-4 px-2 py-1 bg-white/10 text-white/50 text-xs rounded">Page 3</div>

                    <h2
                      className="text-xl font-semibold pb-2 mb-4 border-b-2 text-white"
                      style={{ borderColor: gameBranding?.primaryColor || '#a855f7' }}
                    >
                      Streaming Content Performance
                    </h2>

                    {/* Streaming Metrics - Uses adjustedMetrics and respects visibility */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      {[
                        { key: 'avgCcv', label: 'Combined CCV', value: formatFullNumber(adjustedMetrics.streamMetrics.averageCcv), accent: '#a855f7' },
                        { key: 'peakCcv', label: 'Combined Peak CCV', value: formatFullNumber(adjustedMetrics.streamMetrics.peakCcvMax), accent: '#8b5cf6' },
                        { key: 'watchHours', label: 'Watch Hours', value: formatWatchHours(adjustedMetrics.streamMetrics.totalWatchHours), accent: '#06b6d4' },
                        { key: 'vodViews', label: 'VOD Views', value: formatFullNumber(adjustedMetrics.streamMetrics.totalVodViews), accent: '#f97316' },
                        { key: 'streamCount', label: 'Streams', value: adjustedMetrics.streamMetrics.count, accent: '#22c55e' },
                      ].filter(metric => metricVisibility[metric.key] !== false).map((metric, idx) => (
                        <div
                          key={idx}
                          className="p-3 rounded-lg relative overflow-hidden bg-gray-800 border border-gray-700"
                        >
                          <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full opacity-20 blur-xl pointer-events-none" style={{ background: metric.accent }} />
                          <p className="text-xs text-white/50 mb-1 relative z-10">{metric.label}</p>
                          <p className="text-lg font-bold text-white relative z-10">
                            {metric.value}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* Streaming Timeline Chart - Platform Lines */}
                    {reportData.streamingSection.timeline?.length > 0 && (() => {
                      const hasTwitch = reportData.streamingSection.timeline.some(d => d.twitch?.avgCcv > 0);
                      const hasYouTube = reportData.streamingSection.timeline.some(d => d.youtube?.avgCcv > 0);
                      const hasAggregateAvgCcv = reportData.streamingSection.timeline.some(d => d.avgCcv > 0);
                      const hasPlatformData = hasTwitch || hasYouTube;
                      const chartData = reportData.streamingSection.timeline.map(d => ({
                        ...d,
                        twitchAvgCcv: d.twitch?.avgCcv || 0,
                        youtubeAvgCcv: d.youtube?.avgCcv || 0
                      }));

                      return (
                        <div className="mb-6">
                          <p className="text-sm font-medium text-white/70 mb-2">Streaming CCV Over Time {hasPlatformData ? '(by Platform)' : ''}</p>
                          <div style={{ width: '100%', height: '200px' }}>
                            <ResponsiveContainer width="100%" height={180}>
                              <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="period" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} />
                                <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} tickFormatter={formatNumber} />
                                <Tooltip formatter={(value) => formatNumber(value)} contentStyle={{ backgroundColor: 'rgba(0,0,0,0.85)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff' }} itemStyle={{ color: '#fff' }} labelStyle={{ color: 'rgba(255,255,255,0.6)' }} />
                                <Legend wrapperStyle={{ color: 'rgba(255,255,255,0.7)' }} />
                                {hasTwitch && <Line type="monotone" dataKey="twitchAvgCcv" name="Twitch" stroke="#9146FF" strokeWidth={2} dot={{ fill: '#9146FF', r: 2 }} connectNulls />}
                                {hasYouTube && <Line type="monotone" dataKey="youtubeAvgCcv" name="YouTube" stroke="#FF0000" strokeWidth={2} dot={{ fill: '#FF0000', r: 2 }} connectNulls />}
                                {!hasPlatformData && hasAggregateAvgCcv && <Line type="monotone" dataKey="avgCcv" name="Combined CCV" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 2 }} connectNulls />}
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      );
                    })()}

                    {formData.streamingAnalysis && (
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold mb-1" style={{ color: gameBranding?.primaryColor || '#a855f7' }}>
                          Streaming Content Analysis
                        </h3>
                        <p className="text-white/70 text-sm whitespace-pre-wrap">{formData.streamingAnalysis}</p>
                      </div>
                    )}

                    {/* Streaming Content by Platform - Card Grid Layout (matches editor) */}
                    {(() => {
                      // Sort function matching ContentGridView
                      const sortContent = (arr, sortKey) => {
                        return [...arr].sort((a, b) => {
                          switch (sortKey) {
                            case 'date-newest': return new Date(b.postDate) - new Date(a.postDate);
                            case 'date-oldest': return new Date(a.postDate) - new Date(b.postDate);
                            case 'views-high': return (b.views || 0) - (a.views || 0);
                            case 'avgCcv-high': return (b.avgCcv || 0) - (a.avgCcv || 0);
                            case 'peakCcv-high': return (b.peakCcv || 0) - (a.peakCcv || 0);
                            case 'watchHours-high': return (b.watchHours || 0) - (a.watchHours || 0);
                            default: return 0;
                          }
                        });
                      };
                      const filteredStreams = (reportData.streamingSection.content || []).filter(s => !excludedContentIds.includes(s.id));
                      const groupedByPlatform = {};
                      filteredStreams.forEach(stream => {
                        const platform = stream.platform?.toLowerCase() || 'other';
                        if (!groupedByPlatform[platform]) groupedByPlatform[platform] = [];
                        groupedByPlatform[platform].push(stream);
                      });
                      // Apply sorting to each platform group
                      Object.keys(groupedByPlatform).forEach(p => {
                        groupedByPlatform[p] = sortContent(groupedByPlatform[p], streamingSortBy);
                      });
                      const platformOrder = ['twitch', 'youtube', 'kick'];
                      const platformLabels = { youtube: 'YouTube Streams', tiktok: 'TikTok Streams', twitch: 'Twitch Streams', kick: 'Kick Streams', other: 'Other Streams' };
                      const platformColors = { youtube: '#FF0000', tiktok: '#000000', twitch: '#9146FF', kick: '#00E70A', other: '#6B7280' };
                      const platformsToShow = platformOrder.filter(p => groupedByPlatform[p]?.length > 0);

                      return (
                        <div className="space-y-6">
                          {platformsToShow.map(platform => (
                            <div key={platform}>
                              {/* Platform Header */}
                              <div className="flex items-center justify-between bg-gray-800 px-3 py-2 rounded-lg border border-gray-700 mb-3">
                                <h3 className="text-sm font-semibold text-white">
                                  {platformLabels[platform] || `${({ twitch: 'Twitch', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' }[platform] || platform)} Streams`}
                                </h3>
                                <span className="text-xs font-medium text-white/50 bg-white/10 px-2 py-1 rounded">
                                  {groupedByPlatform[platform].length} stream{groupedByPlatform[platform].length !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {/* Stream Card Grid - Limited to 9 per platform */}
                              <div className="grid grid-cols-3 gap-3">
                                {groupedByPlatform[platform].slice(0, 9).map((stream, idx) => (
                                  <div key={idx} className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                                    {/* Thumbnail */}
                                    <div className="relative bg-gray-900 aspect-video">
                                      {stream.thumbnailUrl ? (
                                        <img src={resolveThumbnailUrl(stream.thumbnailUrl)} alt={stream.title} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                                          <span className="text-gray-500 text-xs">No Thumbnail</span>
                                        </div>
                                      )}
                                      {/* Platform Badge */}
                                      <span
                                        className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-white"
                                        style={{ backgroundColor: platformColors[platform] + 'dd' }}
                                      >
                                        {({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', instagram: 'Instagram' }[platform] || platform)}
                                      </span>
                                      {/* Creator Avatar */}
                                      {stream.creator?.profileImageUrl && (
                                        <img
                                          src={stream.creator.profileImageUrl}
                                          alt=""
                                          className="absolute top-1 left-1 w-5 h-5 rounded-full border border-white object-cover"
                                        />
                                      )}
                                      {/* Paid badge */}
                                      {stream.isPaid && (
                                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-semibold bg-sc-pink text-white">💰 Paid</span>
                                      )}
                                    </div>
                                    {/* Content Details */}
                                    <div className="p-2 space-y-1">
                                      <p className="text-[10px] font-medium text-white line-clamp-2">{stream.title || 'Untitled'}</p>
                                      <p className="text-[9px] text-white/50">{stream.creator?.name}</p>
                                      {/* Stream Metrics */}
                                      <div className="grid grid-cols-2 gap-1 pt-1 border-t border-white/10 text-[9px]">
                                        <div><span className="text-white/50">Combined CCV:</span> <span className="font-medium text-white">{formatFullNumber(stream.avgCcv)}</span></div>
                                        <div><span className="text-white/50">Combined Peak:</span> <span className="font-medium text-white">{formatFullNumber(stream.peakCcv)}</span></div>
                                        <div><span className="text-white/50">Watch Hrs:</span> <span className="font-medium text-white">{formatWatchHours(stream.watchHours)}</span></div>
                                        <div><span className="text-white/50">VOD Views:</span> <span className="font-medium text-white">{formatFullNumber(stream.vodViews)}</span></div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* After Streaming Image Block */}
                    {contentBlocks.filter(b => b.position === 'after-streaming').map((block, idx) => (
                      <img key={idx} src={block.src} alt="" className="w-full h-44 object-cover rounded-lg my-4" />
                    ))}
                  </div>
                )}

                {/* Page 4: Platform Breakdown */}
                {reportData?.platforms?.length > 0 && (() => {
                  const fmtPlatform = (p) => ({ twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', twitter: 'Twitter', instagram: 'Instagram' }[p?.toLowerCase()] || p);
                  const platforms = safeReportData.platforms || [];
                  const totalViews = platforms.reduce((sum, p) => sum + (p.views || 0), 0);

                  const platformSplitData = platforms.map(p => ({
                    name: fmtPlatform(p.platform || p.name),
                    value: p.views || 0,
                    color: getPlatformColor(p.platform || p.name),
                    percentage: totalViews > 0 ? ((p.views / totalViews) * 100).toFixed(1) : '0',
                  }));

                  // Creator coverage - all time
                  const creators = safeReportData.creators || [];
                  const covered = creators.filter(c => (c.metrics?.totalContentCount || c.metrics?.contentCount || 0) > 0).length;
                  const pending = creators.length - covered;
                  const coverageTotal = covered + pending;
                  const coverageData = [
                    { name: 'Covered', value: covered, color: '#22c55e', percentage: coverageTotal > 0 ? ((covered / coverageTotal) * 100).toFixed(0) : 0 },
                    { name: 'Pending', value: pending, color: '#4b5563', percentage: coverageTotal > 0 ? ((pending / coverageTotal) * 100).toFixed(0) : 0 },
                  ];

                  // Region breakdown - lookup from creators, count from content in period
                  const creatorRegionLookup = {};
                  for (const c of creators) {
                    if (c.creator?.id) {
                      creatorRegionLookup[c.creator.id] = c.creator.region || c.creator.country || 'Unknown';
                    }
                  }
                  const allContent = [
                    ...(safeReportData.videoSection?.content || []),
                    ...(safeReportData.streamingSection?.content || []),
                  ];
                  const regionCounts = {};
                  const seenCreators = new Set();
                  for (const c of allContent) {
                    const creatorId = c.creatorId || c.creator?.id;
                    const cName = c.creatorName || c.creator?.name;
                    if ((!creatorId && !cName) || seenCreators.has(creatorId || cName)) continue;
                    seenCreators.add(creatorId || cName);
                    const region = (creatorId && creatorRegionLookup[creatorId]) || c.creatorRegion || c.creator?.region || c.creator?.country || 'Unknown';
                    regionCounts[region] = (regionCounts[region] || 0) + 1;
                  }
                  const regionColors = ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
                  const sortedRegions = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);
                  const regionCreatorCount = seenCreators.size;
                  const topRegions = sortedRegions.slice(0, 5);
                  const otherCount = sortedRegions.slice(5).reduce((sum, [, count]) => sum + count, 0);
                  const regionData = topRegions.map(([name, value], i) => ({
                    name, value, color: regionColors[i % regionColors.length],
                    percentage: regionCreatorCount > 0 ? ((value / regionCreatorCount) * 100).toFixed(0) : 0,
                  }));
                  if (otherCount > 0) regionData.push({ name: 'Other', value: otherCount, color: '#6b7280', percentage: regionCreatorCount > 0 ? ((otherCount / regionCreatorCount) * 100).toFixed(0) : 0 });

                  const PreviewDonutChart = ({ data, centerValue, centerLabel, title }) => (
                    <div className="flex flex-col items-center bg-gray-800 border border-gray-700 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-white/60 mb-1">{title}</h4>
                      <div className="relative" style={{ width: 120, height: 120 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={data} cx="50%" cy="50%" innerRadius={35} outerRadius={50} paddingAngle={2} dataKey="value" stroke="none">
                              {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                          <span className="text-sm font-bold text-white">{centerValue}</span>
                          <span className="text-[9px] text-white/50">{centerLabel}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap justify-center gap-x-2 gap-y-0.5 mt-1">
                        {data.map((entry, i) => (
                          <div key={i} className="flex items-center gap-0.5 text-[9px]">
                            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-white/50">{entry.name} ({entry.percentage}%)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );

                  return (
                    <div
                      className="bg-gray-900 border border-gray-700 rounded-xl shadow-lg p-8 relative"
                      style={{ fontFamily: gameBranding?.fontFamily || 'Helvetica' }}
                    >
                      <div className="absolute top-4 right-4 px-2 py-1 bg-white/10 text-white/50 text-xs rounded">Page 4</div>

                      <h2
                        className="text-xl font-semibold pb-2 mb-4 border-b-2 text-white"
                        style={{ borderColor: gameBranding?.secondaryColor || '#3b82f6' }}
                      >
                        Platform Breakdown
                      </h2>

                      {/* Platform Cards */}
                      <div className="grid grid-cols-4 gap-2 mb-6">
                        {platforms.map(platform => {
                          const pName = platform.platform || platform.name || '';
                          const color = getPlatformColor(pName);
                          const pct = totalViews > 0 ? ((platform.views / totalViews) * 100).toFixed(1) : '0';
                          return (
                            <div key={pName} className="bg-gray-800 border border-gray-700 rounded-lg p-3" style={{ borderBottom: `2px solid ${color}` }}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <PlatformIcon platform={pName} size="sm" />
                                <span className="text-xs text-white/60">{fmtPlatform(pName)}</span>
                              </div>
                              <div className="text-sm font-bold text-white mb-1">{formatFullNumber(platform.views)}</div>
                              <div className="flex items-center gap-1">
                                <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                                </div>
                                <span className="text-[9px] font-medium" style={{ color }}>{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Insights Donut Charts */}
                      <div className="grid grid-cols-3 gap-3">
                        <PreviewDonutChart
                          data={coverageData}
                          centerValue={coverageTotal > 0 ? `${((covered / coverageTotal) * 100).toFixed(0)}%` : '0%'}
                          centerLabel="Coverage"
                          title="Creator Coverage"
                        />
                        {platformSplitData.length > 0 && (
                          <PreviewDonutChart
                            data={platformSplitData}
                            centerValue={formatFullNumber(totalViews)}
                            centerLabel="Total Views"
                            title="Platform Split"
                          />
                        )}
                        {regionData.length > 0 && (
                          <PreviewDonutChart
                            data={regionData}
                            centerValue={regionCreatorCount.toString()}
                            centerLabel="Creators"
                            title="Region Breakdown"
                          />
                        )}
                      </div>

                      {/* After Platforms Image Block */}
                      {contentBlocks.filter(b => b.position === 'after-platforms').map((block, idx) => (
                        <img key={idx} src={block.src} alt="" className="w-full h-44 object-cover rounded-lg my-4" />
                      ))}
                    </div>
                  );
                })()}

              </div>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ClientReportEditor;
